import {
  SHOP_NAMESPACE,
  type ContentGroup,
  type ContentManifest,
} from '@/features/storefront/content/types';

/**
 * The words the shop itself renders, in every theme.
 *
 * A theme draws the cart; it does not write "Your cart is empty". That line,
 * the checkout's field labels, the search page's "Nothing matched", the
 * catalog's empty shelf - all of it is the storefront's own copy, shared by
 * all nine themes, and all of it was hard-coded until now. It is declared here
 * so a merchant can change it once and have it change everywhere, without
 * touching a theme.
 *
 * A theme adds its *own* copy on top of this - see `previewThemes/<id>/
 * content.ts` - which is why the two are separate manifests rather than one
 * long file: Hyper has a hundred strings of its own and Classic has none, and
 * neither of those facts should be able to disturb the other's shop.
 *
 * Where a section already owns a piece of copy - a cart's `title`, a 404's
 * `body` - the section still wins. These are the words underneath: what
 * renders for a merchant who has not overridden the section either, which for
 * most shops is every page but the home page.
 */
const groups: readonly ContentGroup[] = [
  // --- Header -------------------------------------------------------------
  {
    id: 'header-actions',
    area: 'header',
    label: 'Shopper actions',
    description: 'The links in the top-right corner of every page.',
    fields: [
      { key: 'header.search', label: 'Search link', value: 'Search', role: 'label', max: 30 },
      { key: 'header.sign_in', label: 'Sign in link', value: 'Sign in', role: 'label', max: 30 },
      {
        key: 'header.account',
        label: 'Account link',
        value: 'My account',
        role: 'label',
        hint: 'Shown once a shopper is signed in.',
        max: 30,
      },
      { key: 'header.cart', label: 'Cart link', value: 'Cart', role: 'label', max: 30 },
    ],
  },
  {
    id: 'header-menu',
    area: 'header',
    label: 'Mobile menu',
    description: 'The slide-out menu a shopper opens on a phone.',
    fields: [
      { key: 'header.menu', label: 'Open menu', value: 'Menu', role: 'label', max: 30 },
      { key: 'header.menu_close', label: 'Close menu', value: 'Close menu', role: 'label', max: 30 },
      {
        key: 'header.menu_categories',
        label: 'Menu title with no menu built',
        value: 'Categories',
        role: 'label',
        hint: 'Used until you build a navigation menu of your own.',
        max: 30,
      },
      {
        key: 'header.more',
        label: 'Menu items that do not fit',
        value: 'More',
        role: 'label',
        hint: 'Opens the menu items there is no room for across the top of a wide screen.',
        max: 20,
      },
    ],
  },
  {
    id: 'header-chrome',
    area: 'header',
    label: 'Announcements and navigation',
    fields: [
      {
        key: 'chrome.show_announcement',
        label: 'Announcement dot',
        value: 'Show announcement {number}',
        role: 'label',
        hint: 'Read aloud by screen readers. {number} is the announcement’s position.',
        max: 60,
      },
      {
        key: 'chrome.nav_pages',
        label: 'Page links',
        value: 'Pages',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 40,
      },
      {
        key: 'chrome.nav_blogs',
        label: 'Blog links',
        value: 'Blogs',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 40,
      },
      { key: 'chrome.departments', label: 'Departments heading', value: 'Departments', role: 'heading', max: 40 },
      {
        key: 'chrome.deliver_to',
        label: 'Delivery PIN code button',
        value: 'Deliver to {pincode}',
        role: 'button',
        hint: 'Shown in the header when the delivery PIN code box is on. {pincode} is the customer’s PIN code.',
        max: 40,
      },
      {
        key: 'chrome.set_pincode',
        label: 'Delivery PIN code button, none set',
        value: 'Set delivery pincode',
        role: 'button',
        max: 40,
      },
      {
        key: 'chrome.pincode_heading',
        label: 'Delivery PIN code box heading',
        value: 'Where should we deliver?',
        role: 'heading',
        max: 60,
      },
      {
        key: 'chrome.we_deliver',
        label: 'PIN code delivered to',
        value: 'We deliver to {pincode}',
        role: 'message',
        hint: '{pincode} is the PIN code the customer checked.',
        max: 80,
      },
      {
        key: 'chrome.no_delivery',
        label: 'PIN code not delivered to',
        value: 'We don’t deliver to {pincode} yet',
        role: 'message',
        hint: '{pincode} is the PIN code the customer checked.',
        max: 80,
      },
      { key: 'chrome.pincode_close', label: 'Close PIN code box', value: 'Close', role: 'label', max: 30 },
    ],
  },

  // --- Cart ---------------------------------------------------------------
  {
    id: 'cart-page',
    area: 'cart',
    label: 'The basket',
    fields: [
      { key: 'cart.title', label: 'Heading', value: 'Your cart', role: 'heading', max: 80 },
      {
        key: 'cart.empty',
        label: 'Empty basket',
        value: 'Your cart is empty.',
        role: 'empty',
        max: 200,
      },
      {
        key: 'cart.continue',
        label: 'Keep shopping button',
        value: 'Continue shopping',
        role: 'button',
        max: 40,
      },
      {
        key: 'cart.low_stock',
        label: 'Not enough stock',
        value: 'Only {available} left - reduce the quantity to check out.',
        role: 'validation',
        hint: '{available} is replaced by how many are left.',
        max: 160,
      },
      {
        key: 'cart.over_stock_checkout',
        label: 'Over stock at checkout',
        value: 'Some items have fewer left than you have chosen. Reduce them to check out.',
        role: 'validation',
        max: 200,
      },
      { key: 'cart.remove', label: 'Remove a line', value: 'Remove', role: 'button', max: 30 },
      {
        key: 'cart.quantity',
        label: 'Quantity field',
        value: 'Quantity for {product}',
        role: 'label',
        hint: 'Read aloud by screen readers. {product} is the product name.',
        max: 80,
      },
    ],
  },
  {
    id: 'cart-drawer',
    area: 'cart',
    label: 'Quick cart drawer',
    fields: [
      {
        key: 'cart.drawer_title',
        label: 'Drawer heading',
        value: 'Your cart ({count})',
        role: 'heading',
        hint: '{count} is replaced by how many items are in the cart.',
        max: 60,
      },
      { key: 'cart.drawer_close', label: 'Close drawer', value: 'Close cart', role: 'label', max: 30 },
      { key: 'cart.view_cart', label: 'View cart button', value: 'View cart', role: 'button', max: 30 },
      {
        key: 'cart.free_shipping_remaining',
        label: 'Free shipping progress',
        value: 'Spend {amount} more for free shipping!',
        role: 'label',
        hint: '{amount} is replaced by how much more the shopper needs to spend.',
        max: 100,
      },
      {
        key: 'cart.free_shipping_unlocked',
        label: 'Free shipping reached',
        value: 'You have unlocked free shipping!',
        role: 'label',
        max: 100,
      },
      {
        key: 'cart.drawer_recommended',
        label: 'Suggestions heading',
        value: 'You may also like',
        role: 'heading',
        max: 40,
      },
      {
        key: 'cart.drawer_previous',
        label: 'Suggestions: back arrow',
        value: 'Previous products',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 40,
      },
      {
        key: 'cart.drawer_next',
        label: 'Suggestions: forward arrow',
        value: 'More products',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 40,
      },
      {
        key: 'cart.drawer_note',
        label: 'Note under the subtotal',
        value: 'Taxes and shipping calculated at checkout.',
        role: 'label',
        max: 120,
      },
    ],
  },
  {
    id: 'cart-summary',
    area: 'cart',
    label: 'Totals panel',
    fields: [
      { key: 'cart.summary', label: 'Heading', value: 'Summary', role: 'heading', max: 40 },
      { key: 'cart.subtotal', label: 'Subtotal', value: 'Subtotal', role: 'label', max: 40 },
      { key: 'cart.discount', label: 'Discount line', value: 'Discount', role: 'label', max: 40 },
      {
        key: 'cart.bundle_discount',
        label: 'Bundle discount line',
        value: 'Bundle discount',
        role: 'label',
        hint: 'Shown in the cart, the cart drawer, checkout and the order when a "Frequently bought together" set is in the cart.',
        max: 40,
      },
      { key: 'cart.tax', label: 'Tax line', value: 'GST included', role: 'label', max: 40 },
      { key: 'cart.shipping', label: 'Delivery line', value: 'Delivery', role: 'label', max: 40 },
      {
        key: 'cart.shipping_free',
        label: 'Delivery when it is free',
        value: 'Free',
        role: 'label',
        max: 40,
      },
      {
        key: 'cart.shipping_pending',
        label: 'Delivery before a PIN code',
        value: 'Enter your PIN code to see delivery charges.',
        role: 'message',
        max: 120,
      },
      {
        key: 'cart.check_delivery',
        label: 'PIN code field',
        value: 'Check delivery',
        role: 'label',
        max: 40,
      },
      {
        key: 'cart.check_delivery_placeholder',
        label: 'PIN code hint',
        value: 'PIN code',
        role: 'placeholder',
        max: 30,
      },
      {
        key: 'cart.check_delivery_button',
        label: 'Check button',
        value: 'Check',
        role: 'button',
        max: 30,
      },
      { key: 'cart.total', label: 'Total', value: 'Total', role: 'label', max: 40 },
      {
        key: 'cart.discount_label',
        label: 'Discount code field',
        value: 'Discount code',
        role: 'label',
        max: 40,
      },
      {
        key: 'cart.discount_placeholder',
        label: 'Discount code hint',
        value: 'SAVE20',
        role: 'placeholder',
        max: 30,
      },
      { key: 'cart.discount_apply', label: 'Apply button', value: 'Apply', role: 'button', max: 30 },
      {
        key: 'cart.discount_error',
        label: 'Code refused',
        value: 'That code could not be applied.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'cart.discount_error_unknown_code',
        label: 'Code refused: no such code',
        value: 'We could not find that discount code.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'cart.note_label',
        label: 'Order notes heading',
        value: 'Order instructions',
        role: 'label',
        max: 60,
      },
      {
        key: 'cart.note_placeholder',
        label: 'Order notes hint',
        value: 'Delivery or gift notes',
        role: 'placeholder',
        max: 120,
      },
      { key: 'cart.note_saved', label: 'Order notes saved', value: 'Saved', role: 'message', max: 40 },
      {
        key: 'cart.note_error',
        label: 'Order notes not saved',
        value: 'Your note could not be saved. Please try again.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'cart.discount_error_not_started',
        label: 'Code refused: not started yet',
        value: 'This discount code is not active yet.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'cart.discount_error_expired',
        label: 'Code refused: expired',
        value: 'This discount code has expired.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'cart.discount_error_usage_limit_reached',
        label: 'Code refused: used up',
        value: 'This discount code has reached its usage limit.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'cart.discount_error_below_minimum',
        label: 'Code refused: order too small',
        value: 'Add items worth {amount} or more to use this code.',
        role: 'validation',
        hint: '{amount} is replaced by the minimum order value.',
        max: 160,
      },
      {
        key: 'cart.discount_error_below_minimum_quantity',
        label: 'Code refused: too few items',
        value: 'Add at least {quantity} items to use this code.',
        role: 'validation',
        hint: '{quantity} is replaced by the minimum number of items.',
        max: 160,
      },
      {
        key: 'cart.discount_error_not_eligible',
        label: 'Code refused: not for this shopper',
        value: 'This discount code is not available for your account.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'cart.discount_error_country_not_covered',
        label: 'Code refused: country not covered',
        value: 'This discount code does not cover your delivery country.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'cart.discount_error_no_eligible_items',
        label: 'Code refused: nothing in the cart qualifies',
        value: 'None of the items in your cart qualify for this code.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'cart.discount_error_already_used',
        label: 'Code refused: already used',
        value: 'You have already used this discount code.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'cart.discount_applied',
        label: 'Code applied',
        value: 'Code {code} applied',
        role: 'message',
        hint: '{code} is replaced by the discount code.',
        max: 80,
      },
      {
        key: 'cart.discount_remove',
        label: 'Remove code button',
        value: 'Remove',
        role: 'button',
        max: 30,
      },
      { key: 'cart.checkout', label: 'Checkout button', value: 'Checkout', role: 'button', max: 40 },
    ],
  },

  // --- Checkout -----------------------------------------------------------
  {
    id: 'checkout-page',
    area: 'checkout',
    label: 'The till',
    fields: [
      { key: 'checkout.title', label: 'Heading', value: 'Checkout', role: 'heading', max: 80 },
      {
        key: 'checkout.subtitle_online',
        label: 'Line under the heading (online payments on)',
        value: 'Secure payment via Razorpay · UPI, cards, netbanking, or cash on delivery',
        role: 'message',
        multiline: true,
        max: 200,
      },
      {
        key: 'checkout.subtitle_cod',
        label: 'Line under the heading (cash only)',
        value: 'Cash on delivery · pay for your order when it arrives',
        role: 'message',
        multiline: true,
        max: 200,
      },
      {
        key: 'checkout.empty_title',
        label: 'Nothing to buy',
        value: 'Your cart is empty',
        role: 'empty',
        max: 80,
      },
      {
        key: 'checkout.empty_link',
        label: 'Nothing to buy, link',
        value: 'Browse the store',
        role: 'button',
        max: 40,
      },
      {
        key: 'checkout.signin_prompt',
        label: 'Sign-in prompt',
        value: 'Have an account?',
        role: 'message',
        max: 80,
      },
      {
        key: 'checkout.signin_link',
        label: 'Sign-in prompt, link',
        value: 'Sign in',
        role: 'button',
        max: 40,
      },
      {
        key: 'checkout.signin_suffix',
        label: 'Sign-in prompt, after the link',
        value: 'to use your saved addresses.',
        role: 'message',
        max: 120,
      },
    ],
  },
  {
    id: 'checkout-steps',
    area: 'checkout',
    label: 'The three steps',
    fields: [
      { key: 'checkout.step_contact', label: 'Step 1', value: 'Contact', role: 'heading', max: 40 },
      {
        key: 'checkout.step_address',
        label: 'Step 2',
        value: 'Delivery address',
        role: 'heading',
        max: 40,
      },
      {
        key: 'checkout.step_delivery',
        label: 'Delivery step',
        value: 'Delivery',
        role: 'heading',
        hint: 'Only shown by shops that price delivery by PIN code.',
        max: 40,
      },
      { key: 'checkout.step_payment', label: 'Step 3', value: 'Payment', role: 'heading', max: 40 },
    ],
  },
  {
    id: 'checkout-delivery',
    area: 'checkout',
    label: 'Delivery options',
    description: 'Shown by shops that price delivery by PIN code.',
    fields: [
      {
        key: 'checkout.delivery_enter_pincode',
        label: 'Before a PIN code',
        value: 'Enter your PIN code above to see delivery options.',
        role: 'message',
        max: 120,
      },
      {
        key: 'checkout.delivery_loading',
        label: 'Checking',
        value: 'Checking delivery options…',
        role: 'message',
        max: 60,
      },
      {
        key: 'checkout.delivery_unserviceable',
        label: 'Cannot deliver there',
        value: 'Sorry, we cannot deliver to this PIN code yet.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'checkout.delivery_error',
        label: 'Could not check',
        value: 'We could not check delivery to this PIN code. Please try again.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'checkout.delivery_free',
        label: 'Option that costs nothing',
        value: 'Free',
        role: 'label',
        max: 30,
      },
      {
        key: 'checkout.delivery_arrives',
        label: 'Arrival date',
        value: 'Arrives by {date}',
        role: 'label',
        hint: '{date} is replaced by the courier’s estimate.',
        max: 60,
      },
      {
        key: 'checkout.delivery_days',
        label: 'Arrival in days',
        value: '{days} days',
        role: 'label',
        hint: '{days} is replaced by the number of days.',
        max: 40,
      },
      {
        key: 'checkout.delivery_day',
        label: 'Arrival in one day',
        value: '1 day',
        role: 'label',
        max: 40,
      },
      {
        key: 'checkout.delivery_cod_unavailable',
        label: 'Option that cannot take cash',
        value: 'Cash on delivery not available',
        role: 'label',
        max: 80,
      },
      {
        key: 'checkout.free_shipping_nudge',
        label: 'Free delivery nudge',
        value: 'Add {amount} more for free delivery',
        role: 'promo',
        hint: '{amount} is replaced by what is left to spend.',
        max: 100,
      },
    ],
  },
  {
    id: 'checkout-fields',
    area: 'checkout',
    label: 'Form labels',
    fields: [
      { key: 'checkout.field_name', label: 'Name', value: 'Full name', role: 'label', max: 40 },
      {
        key: 'checkout.save_address',
        label: 'Save address',
        value: 'Save this address to my account',
        role: 'label',
        max: 80,
      },
      {
        key: 'checkout.awaiting_payment',
        label: 'Order awaiting payment',
        value:
          'Your order {number} for {amount} is saved but has not been paid for. Pay now to complete it; nothing is dispatched until the payment goes through.',
        role: 'message',
        hint: '{number} is replaced by the order number, {amount} by its total.',
        max: 300,
      },
      { key: 'checkout.field_email', label: 'Email', value: 'Email', role: 'label', max: 40 },
      {
        key: 'checkout.field_phone',
        label: 'Phone',
        value: 'Mobile (optional)',
        role: 'label',
        max: 40,
      },
      {
        key: 'checkout.field_phone_placeholder',
        label: 'Phone hint',
        value: '+91 98765 43210',
        role: 'placeholder',
        max: 40,
      },
      {
        key: 'checkout.field_line1',
        label: 'Street',
        value: 'Flat, building, street',
        role: 'label',
        max: 60,
      },
      {
        key: 'checkout.field_line2',
        label: 'Landmark',
        value: 'Landmark (optional)',
        role: 'label',
        max: 60,
      },
      {
        key: 'checkout.field_line2_placeholder',
        label: 'Landmark hint',
        value: 'Near the metro station',
        role: 'placeholder',
        max: 60,
      },
      {
        key: 'checkout.field_note',
        label: 'Order instructions',
        value: 'Order instructions (optional)',
        role: 'label',
        max: 60,
      },
      { key: 'checkout.field_city', label: 'City', value: 'City', role: 'label', max: 40 },
      { key: 'checkout.field_state', label: 'State', value: 'State', role: 'label', max: 40 },
      { key: 'checkout.field_postal', label: 'PIN code', value: 'PIN code', role: 'label', max: 40 },
      {
        key: 'checkout.address_legend',
        label: 'Saved addresses',
        value: 'Choose a delivery address',
        role: 'label',
        max: 60,
      },
      {
        key: 'checkout.address_default',
        label: 'Default address mark',
        value: 'Default',
        role: 'label',
        max: 30,
      },
      {
        key: 'checkout.address_other',
        label: 'A different address',
        value: 'Deliver somewhere else',
        role: 'label',
        max: 60,
      },
      {
        key: 'checkout.marketing',
        label: 'Marketing opt-in',
        value: 'Send me offers and updates on WhatsApp. You can opt out any time.',
        role: 'message',
        multiline: true,
        max: 200,
      },
    ],
  },
  {
    id: 'checkout-validation',
    area: 'checkout',
    label: 'When something is wrong',
    description: 'What the form says when a field is missing or malformed.',
    fields: [
      {
        key: 'checkout.error_name',
        label: 'Name missing',
        value: 'Enter your name.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'checkout.error_email',
        label: 'Email missing',
        value: 'Enter your email.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'checkout.error_email_invalid',
        label: 'Email malformed',
        value: 'Enter a valid email address.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'checkout.error_phone',
        label: 'Phone malformed',
        value: 'Enter a valid Indian mobile number.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'checkout.error_line1',
        label: 'Street missing',
        value: 'Enter your address.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'checkout.error_city',
        label: 'City missing',
        value: 'Enter your city.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'checkout.error_state',
        label: 'State missing',
        value: 'Enter your state.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'checkout.error_postal',
        label: 'PIN code malformed',
        value: 'Enter a valid 6-digit PIN code.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'checkout.error_place',
        label: 'The order would not go through',
        value: 'We could not place the order. Try again.',
        role: 'validation',
        max: 200,
      },
    ],
  },
  {
    id: 'checkout-payment',
    area: 'checkout',
    label: 'Ways to pay',
    fields: [
      {
        key: 'checkout.payment_online',
        label: 'Pay online',
        value: 'Pay online — UPI, cards, netbanking',
        role: 'label',
        max: 80,
      },
      {
        key: 'checkout.payment_online_note',
        label: 'Pay online, note',
        value: 'Razorpay',
        role: 'label',
        max: 40,
      },
      {
        key: 'checkout.payment_cod',
        label: 'Cash on delivery',
        value: 'Cash on delivery',
        role: 'label',
        max: 80,
      },
      {
        key: 'checkout.payment_cod_note',
        label: 'Cash on delivery, note',
        value: 'Pay when it arrives',
        role: 'label',
        max: 40,
      },
      {
        key: 'checkout.payment_whatsapp',
        label: 'Order via WhatsApp',
        value: 'Order via WhatsApp',
        role: 'label',
        max: 80,
      },
      {
        key: 'checkout.payment_whatsapp_note',
        label: 'Order via WhatsApp, note',
        value: "We'll confirm your order and payment on WhatsApp",
        role: 'label',
        max: 80,
      },
      {
        key: 'checkout.fee_badge',
        label: 'Fee badge',
        value: '+ {amount}',
        role: 'label',
        hint: '{amount} is replaced by the fee for that way of paying.',
        max: 30,
      },
      {
        key: 'checkout.paying_by',
        label: 'The only way to pay',
        value: 'Paying by',
        role: 'label',
        hint: 'Shown before the method when the shop offers just one way to pay.',
        max: 40,
      },
      {
        key: 'checkout.cod_min',
        label: 'Cash on delivery, order too small',
        value: 'Cash on delivery is available on orders of {amount} or more.',
        role: 'validation',
        hint: '{amount} is replaced by the smallest order that can pay in cash.',
        max: 160,
      },
      {
        key: 'checkout.cod_max',
        label: 'Cash on delivery, order too large',
        value: 'Cash on delivery is available on orders up to {amount}.',
        role: 'validation',
        hint: '{amount} is replaced by the largest order that can pay in cash.',
        max: 160,
      },
      {
        key: 'checkout.no_payment_methods',
        label: 'No way to pay',
        value: 'This store is not accepting orders right now. Please try again later.',
        role: 'validation',
        multiline: true,
        max: 200,
      },
      {
        key: 'checkout.payment_unavailable',
        label: 'Chosen way to pay refused',
        value: 'That payment method is not available. Please choose another.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'checkout.cod_not_serviceable',
        label: 'Cash refused for the PIN code',
        value: 'Cash on delivery is not available for this PIN code.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'checkout.gift_covered',
        label: 'Gift card covers it all',
        value: 'Your gift card covers this order in full. There is nothing left to pay.',
        role: 'message',
        multiline: true,
        max: 200,
      },
    ],
  },
  {
    id: 'checkout-giftcard',
    area: 'checkout',
    label: 'Gift cards',
    fields: [
      {
        key: 'checkout.gift_placeholder',
        label: 'Gift card field',
        value: 'Gift card',
        role: 'placeholder',
        max: 40,
      },
      { key: 'checkout.gift_apply', label: 'Apply button', value: 'Apply', role: 'button', max: 30 },
      {
        key: 'checkout.gift_applying',
        label: 'Apply button, working',
        value: 'Applying…',
        role: 'button',
        max: 30,
      },
      {
        key: 'checkout.gift_applied',
        label: 'Applied',
        value: 'Gift card applied',
        role: 'message',
        max: 60,
      },
      {
        key: 'checkout.gift_amount',
        label: 'Applied, the detail line',
        value: '{code} · {amount} off this order',
        role: 'message',
        hint: '{code} and {amount} are replaced by the card and what it pays.',
        max: 120,
      },
      {
        key: 'checkout.gift_remove',
        label: 'Remove button',
        value: 'Remove',
        role: 'button',
        max: 30,
      },
      {
        key: 'checkout.gift_error_unknown_code',
        label: 'No such card',
        value: 'We could not find that gift card.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'checkout.gift_error_disabled',
        label: 'Card switched off',
        value: 'This gift card is no longer active.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'checkout.gift_error_expired',
        label: 'Card expired',
        value: 'This gift card has expired.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'checkout.gift_error_no_balance',
        label: 'Card spent',
        value: 'This gift card has no balance left.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'checkout.gift_error',
        label: 'Anything else',
        value: 'We could not apply that gift card.',
        role: 'validation',
        max: 160,
      },
    ],
  },
  {
    id: 'checkout-summary',
    area: 'checkout',
    label: 'Order summary',
    fields: [
      { key: 'checkout.summary', label: 'Heading', value: 'Your order', role: 'heading', max: 40 },
      { key: 'checkout.subtotal', label: 'Subtotal', value: 'Subtotal', role: 'label', max: 40 },
      { key: 'checkout.discount', label: 'Discount', value: 'Discount', role: 'label', max: 40 },
      { key: 'checkout.tax', label: 'Tax line', value: 'GST included', role: 'label', max: 40 },
      { key: 'checkout.shipping', label: 'Shipping', value: 'Shipping', role: 'label', max: 40 },
      {
        key: 'checkout.shipping_free',
        label: 'Shipping when it is free',
        value: 'Complimentary',
        role: 'label',
        max: 40,
      },
      {
        key: 'checkout.shipping_pending',
        label: 'Shipping before a PIN code',
        value: 'Calculated after you enter your pincode',
        role: 'label',
        max: 60,
      },
      { key: 'checkout.total', label: 'Total', value: 'Total', role: 'label', max: 40 },
      {
        key: 'checkout.gift_card',
        label: 'Gift card line',
        value: 'Gift card',
        role: 'label',
        max: 40,
      },
      {
        key: 'checkout.cod_fee',
        label: 'Cash on delivery fee line',
        value: 'Cash on delivery fee',
        role: 'label',
        max: 40,
      },
      {
        key: 'checkout.amount_due',
        label: 'Left to pay',
        value: 'Amount due',
        role: 'label',
        max: 40,
      },
      {
        key: 'checkout.place_order',
        label: 'Place order button',
        value: 'Place order',
        role: 'button',
        max: 40,
      },
      {
        key: 'checkout.placing',
        label: 'Place order button, working',
        value: 'Placing order…',
        role: 'button',
        max: 40,
      },
      {
        key: 'checkout.note_gift',
        label: 'Fine print, gift card',
        value: 'Your gift card settles this order in full. ',
        role: 'legal',
        multiline: true,
        max: 200,
      },
      {
        key: 'checkout.note_online',
        label: 'Fine print, paying online',
        value: 'Payment is confirmed only after your bank verifies it. ',
        role: 'legal',
        multiline: true,
        max: 200,
      },
      {
        key: 'checkout.note_cod',
        label: 'Fine print, cash on delivery',
        value: 'Pay in cash when your order arrives. ',
        role: 'legal',
        multiline: true,
        max: 200,
      },
      {
        key: 'checkout.note_whatsapp',
        label: 'Fine print, ordering via WhatsApp',
        value:
          'Placing the order opens WhatsApp with your order details pre-filled. Nothing is charged here. ',
        role: 'legal',
        multiline: true,
        max: 200,
      },
      {
        key: 'checkout.note_invoice',
        label: 'Fine print, the invoice',
        value: 'A GST invoice reaches your inbox within minutes of dispatch.',
        role: 'legal',
        multiline: true,
        max: 200,
      },
      {
        key: 'checkout.line_quantity',
        label: 'Quantity under a line',
        value: 'Qty {quantity}',
        role: 'label',
        hint: '{quantity} is how many of that item are in the order.',
        max: 30,
      },
    ],
  },

  // --- Search -------------------------------------------------------------
  {
    id: 'search-page',
    area: 'search',
    label: 'Searching',
    fields: [
      { key: 'search.title', label: 'Heading', value: 'Search', role: 'heading', max: 80 },
      {
        key: 'search.placeholder',
        label: 'Search box',
        value: 'Search products',
        role: 'placeholder',
        max: 60,
      },
      { key: 'search.filters', label: 'Filters button', value: 'Filters', role: 'button', max: 40 },
      { key: 'search.sort', label: 'Sort label', value: 'Sort', role: 'label', max: 40 },
      {
        key: 'search.working',
        label: 'While it looks',
        value: 'Searching…',
        role: 'message',
        max: 40,
      },
      {
        key: 'search.count_one',
        label: 'One result',
        value: '{count} product',
        role: 'message',
        max: 60,
      },
      {
        key: 'search.count_many',
        label: 'Several results',
        value: '{count} products',
        role: 'message',
        hint: '{count} is replaced by how many were found.',
        max: 60,
      },
      {
        key: 'search.remove_filter',
        label: 'Remove one filter',
        value: 'Remove filter',
        role: 'label',
        max: 40,
      },
      {
        key: 'search.clear_all',
        label: 'Clear them all',
        value: 'Clear all',
        role: 'button',
        max: 40,
      },
    ],
  },
  {
    id: 'search-empty',
    area: 'search',
    label: 'Nothing found',
    fields: [
      {
        key: 'search.empty_term',
        label: 'Nothing matched the words',
        value: 'Nothing matched “{term}”.',
        role: 'empty',
        hint: '{term} is replaced by what the shopper typed.',
        max: 200,
      },
      {
        key: 'search.empty_filters',
        label: 'Nothing matched the filters',
        value: 'Nothing here matches those filters.',
        role: 'empty',
        max: 200,
      },
      {
        key: 'search.clear_filters',
        label: 'Undo the filters',
        value: 'Clear the filters',
        role: 'button',
        max: 40,
      },
      {
        key: 'search.browse',
        label: 'Go and look instead',
        value: 'Browse everything',
        role: 'button',
        max: 40,
      },
    ],
  },
  {
    id: 'search-suggest',
    area: 'search',
    label: 'Search suggestions',
    description:
      'The box that shows results while shoppers type, when Search suggestions is on in Plugins. Its search box hint and "Searching…" are shared with the search page.',
    fields: [
      {
        key: 'search.suggest_products',
        label: 'Products heading',
        value: 'Products',
        role: 'heading',
        max: 40,
      },
      {
        key: 'search.suggest_see_all',
        label: 'Every result link',
        value: 'See all results for “{term}”',
        role: 'button',
        hint: '{term} is replaced by what the shopper typed.',
        max: 80,
      },
    ],
  },

  // --- Catalog ------------------------------------------------------------
  {
    id: 'listing-page',
    area: 'listing',
    label: 'The catalog',
    fields: [
      { key: 'listing.title', label: 'Heading', value: 'All products', role: 'heading', max: 80 },
      {
        key: 'listing.all',
        label: 'The unfiltered filter',
        value: 'All products',
        role: 'label',
        hint: 'The first chip or row of the category filter.',
        max: 40,
      },
      {
        key: 'listing.all_short',
        label: 'The unfiltered filter, narrow',
        value: 'All',
        role: 'label',
        hint: 'Used where the filter is a tight inline row.',
        max: 20,
      },
      {
        key: 'listing.refine',
        label: 'Link to search',
        value: 'Search & filter',
        role: 'button',
        max: 40,
      },
      {
        key: 'listing.empty_category',
        label: 'An empty shelf',
        value: 'No published products in this category yet.',
        role: 'empty',
        max: 200,
      },
      {
        key: 'listing.empty',
        label: 'An empty shop',
        value: 'This store has not published any products yet.',
        role: 'empty',
        max: 200,
      },
      {
        key: 'listing.shop_by_category',
        label: 'Link to every category',
        value: 'Shop by category →',
        role: 'button',
        max: 40,
      },
      { key: 'listing.all_services', label: 'The unfiltered filter, booking shop', value: 'All services', role: 'button', max: 30 },
      { key: 'listing.all_categories', label: 'Link to every category, booking shop', value: 'All categories →', role: 'button', max: 40 },
      {
        key: 'listing.categories',
        label: 'Category filter name',
        value: 'Categories',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 40,
      },
      {
        key: 'listing.browse',
        label: 'Sidebar filter heading',
        value: 'Browse',
        role: 'heading',
        max: 30,
      },
      {
        key: 'listing.promo_heading',
        label: 'Promo tile heading',
        value: 'New arrivals',
        role: 'heading',
        hint: 'On the advert in the product grid, when its own heading is blank.',
        max: 60,
      },
      {
        key: 'listing.promo_cta',
        label: 'Promo tile button',
        value: 'Shop now',
        role: 'button',
        hint: 'On the advert in the product grid, when it links somewhere and its own button words are blank.',
        max: 30,
      },
    ],
  },
  {
    id: 'category-page',
    area: 'category',
    label: 'The shelves',
    fields: [
      {
        key: 'category.title',
        label: 'Heading',
        value: 'Shop by category',
        role: 'heading',
        max: 80,
      },
      {
        key: 'category.empty',
        label: 'No shelves yet',
        value: 'This store has not published any categories yet.',
        role: 'empty',
        max: 200,
      },
      {
        key: 'category.meta_title',
        label: 'Browser tab title',
        value: 'Categories',
        role: 'label',
        max: 60,
      },
    ],
  },

  // --- Product ------------------------------------------------------------
  {
    id: 'product-buy',
    area: 'product',
    label: 'The buy column',
    description: 'Shared by every theme that does not draw its own product page.',
    fields: [
      {
        key: 'product.tax_note',
        label: 'Under the price',
        value: 'Inclusive of all taxes',
        role: 'legal',
        max: 80,
      },
      { key: 'product.options', label: 'Options heading', value: 'Options', role: 'label', max: 40 },
      {
        key: 'product.option_standard',
        label: 'A product sold one way',
        value: 'Standard',
        role: 'label',
        max: 40,
      },
      {
        key: 'product.add_to_cart',
        label: 'Add to cart',
        value: 'Add to cart',
        role: 'button',
        max: 40,
      },
      {
        key: 'product.adding',
        label: 'Add to cart, working',
        value: 'Adding…',
        role: 'button',
        max: 40,
      },
      {
        key: 'product.out_of_stock',
        label: 'Out of stock',
        value: 'Out of stock',
        role: 'button',
        max: 40,
      },
      {
        key: 'product.enquire',
        label: 'Enquiry button',
        value: 'Enquire now',
        role: 'button',
        hint: 'Shown instead of Add to cart when your cart is turned off.',
        max: 40,
      },
      {
        key: 'product.zoom',
        label: 'Zoom the picture',
        value: 'Zoom image',
        role: 'label',
        max: 40,
      },
    ],
  },
  {
    id: 'product-detail',
    area: 'product',
    label: 'Under the picture',
    fields: [
      {
        key: 'product.details',
        label: 'Specification heading',
        value: 'Product details',
        role: 'heading',
        max: 60,
      },
      {
        key: 'product.description',
        label: 'Description heading',
        value: 'Description',
        role: 'heading',
        max: 60,
      },
      {
        key: 'product.not_found',
        label: 'Unknown product',
        value: 'Product not found',
        role: 'empty',
        max: 80,
      },
      {
        key: 'product.back_to_store',
        label: 'Unknown product, the way back',
        value: 'Back to the store',
        role: 'button',
        max: 40,
      },
      {
        key: 'product.after_sale_returns',
        label: 'Returns offered',
        value: '{days}-day returns',
        role: 'label',
        max: 40,
      },
      {
        key: 'product.after_sale_refunds',
        label: 'Refunds offered',
        value: '{days}-day refunds',
        role: 'label',
        max: 40,
      },
      {
        key: 'product.after_sale_replacement',
        label: 'Replacement offered',
        value: '{days}-day replacement',
        role: 'label',
        max: 40,
      },
      {
        key: 'product.after_sale_none',
        label: 'No returns on this product',
        value: 'Not returnable',
        role: 'label',
        max: 40,
      },
    ],
  },

  // --- The customer's own way in ------------------------------------------
  {
    id: 'login-page',
    area: 'login',
    label: 'Signing in',
    description: 'The fields are fixed - they are the gate on somebody’s orders - but every word around them is yours.',
    fields: [
      { key: 'login.title', label: 'Heading', value: 'Sign in', role: 'heading', max: 60 },
      {
        key: 'login.intro',
        label: 'Line under the heading',
        value: 'See your orders, track a delivery and check out faster.',
        role: 'message',
        multiline: true,
        max: 200,
      },
      { key: 'login.email', label: 'Email field', value: 'Email address', role: 'label', max: 40 },
      { key: 'login.password', label: 'Password field', value: 'Password', role: 'label', max: 40 },
      {
        key: 'login.forgot',
        label: 'Forgotten password link',
        value: 'Forgot password?',
        role: 'button',
        max: 40,
      },
      { key: 'login.submit', label: 'Sign in button', value: 'Sign in', role: 'button', max: 40 },
      {
        key: 'login.submitting',
        label: 'Sign in button, working',
        value: 'Signing in…',
        role: 'button',
        max: 40,
      },
      {
        key: 'login.register_prompt',
        label: 'Invitation to register',
        value: 'New here?',
        role: 'message',
        max: 60,
      },
      {
        key: 'login.register_link',
        label: 'Invitation to register, link',
        value: 'Create an account',
        role: 'button',
        max: 40,
      },
      {
        key: 'login.optional',
        label: 'Reassurance under it all',
        value: 'You never need an account to buy — it just saves your details for next time.',
        role: 'message',
        multiline: true,
        max: 200,
      },
    ],
  },
  {
    id: 'login-problems',
    area: 'login',
    label: 'When signing in does not work',
    fields: [
      {
        key: 'login.error_email',
        label: 'Email missing',
        value: 'Enter your email address.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'login.error_email_invalid',
        label: 'Email malformed',
        value: 'Enter a valid email address.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'login.error_password',
        label: 'Password missing',
        value: 'Enter your password.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'login.error_generic',
        label: 'Anything else',
        value: 'Something went wrong. Please try again.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'login.captcha',
        label: 'Security check needed',
        value: 'Complete the security check, then sign in.',
        role: 'validation',
        hint: 'Shown after a few wrong passwords, when the "not a robot" check appears.',
        max: 160,
      },
      {
        key: 'login.unverified',
        label: 'Email not confirmed yet',
        value: 'Confirm {email} before signing in.',
        role: 'message',
        hint: '{email} is replaced by the address they typed.',
        max: 160,
      },
      {
        key: 'login.resend',
        label: 'Send the link again',
        value: 'Send the link again',
        role: 'button',
        max: 40,
      },
      {
        key: 'login.resent',
        label: 'The link has gone',
        value: 'We have sent another confirmation link to {email}.',
        role: 'message',
        multiline: true,
        max: 200,
      },
    ],
  },
  {
    id: 'register-page',
    area: 'register',
    label: 'Creating an account',
    fields: [
      {
        key: 'register.title',
        label: 'Heading',
        value: 'Create an account',
        role: 'heading',
        max: 60,
      },
      {
        key: 'register.intro',
        label: 'Line under the heading',
        value: 'Keep your addresses saved and every order in one place.',
        role: 'message',
        multiline: true,
        max: 200,
      },
      { key: 'register.name', label: 'Name field', value: 'Full name', role: 'label', max: 40 },
      { key: 'register.email', label: 'Email field', value: 'Email address', role: 'label', max: 40 },
      {
        key: 'register.password',
        label: 'Password field',
        value: 'Password',
        role: 'label',
        max: 40,
      },
      {
        key: 'register.password_hint',
        label: 'Password advice',
        value: 'At least 10 characters. A short phrase works well.',
        role: 'message',
        max: 160,
      },
      {
        key: 'register.phone',
        label: 'Phone field',
        value: 'Phone (optional)',
        role: 'label',
        max: 40,
      },
      {
        key: 'register.phone_placeholder',
        label: 'Phone hint',
        value: '98765 43210',
        role: 'placeholder',
        max: 40,
      },
      {
        key: 'register.marketing',
        label: 'Marketing opt-in',
        value: 'Send me offers and updates. You can opt out any time.',
        role: 'message',
        multiline: true,
        max: 200,
      },
      {
        key: 'register.submit',
        label: 'Create button',
        value: 'Create account',
        role: 'button',
        max: 40,
      },
      {
        key: 'register.submitting',
        label: 'Create button, working',
        value: 'Creating account…',
        role: 'button',
        max: 40,
      },
      {
        key: 'register.login_prompt',
        label: 'Already registered',
        value: 'Already have an account?',
        role: 'message',
        max: 60,
      },
      {
        key: 'register.login_link',
        label: 'Already registered, link',
        value: 'Sign in',
        role: 'button',
        max: 40,
      },
    ],
  },
  {
    id: 'register-after',
    area: 'register',
    label: 'After they sign up',
    fields: [
      {
        key: 'register.check_email_title',
        label: 'Heading',
        value: 'Check your email',
        role: 'heading',
        max: 60,
      },
      {
        key: 'register.check_email_body',
        label: 'What to do next',
        value:
          'We have sent a confirmation link to {email}. Open it to finish setting up your account — your past orders will be waiting.',
        role: 'message',
        hint: '{email} is replaced by the address they signed up with.',
        multiline: true,
        max: 300,
      },
      {
        key: 'register.back_to_login',
        label: 'The way back',
        value: 'Back to sign in',
        role: 'button',
        max: 40,
      },
      {
        key: 'register.error_name',
        label: 'Name missing',
        value: 'Enter your name.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'register.error_email',
        label: 'Email missing',
        value: 'Enter your email address.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'register.error_email_invalid',
        label: 'Email malformed',
        value: 'Enter a valid email address.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'register.error_password',
        label: 'Password too short',
        value: 'Use at least 10 characters.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'register.error_phone',
        label: 'Phone malformed',
        value: 'Enter a valid Indian mobile number.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'register.error_generic',
        label: 'The account would not be created',
        value: 'We could not create the account. Try again.',
        role: 'validation',
        max: 160,
      },
    ],
  },

  // --- After the till -----------------------------------------------------
  {
    id: 'order-confirmation',
    area: 'checkout',
    label: 'Order confirmation',
    description: 'The page a shopper lands on once an order is placed.',
    fields: [
      { key: 'order.confirmed', label: 'Heading', value: 'Order confirmed', role: 'heading', max: 80 },
      { key: 'order.placed', label: 'Heading, order placed', value: 'Order placed', role: 'heading', max: 80 },
      {
        key: 'order.payment_pending',
        label: 'Heading, payment not finished',
        value: 'Payment not completed',
        role: 'heading',
        max: 80,
      },
      {
        key: 'order.number',
        label: 'Order number',
        value: 'Order {number}',
        role: 'label',
        hint: '{number} is replaced by the order number.',
        max: 60,
      },
      { key: 'order.subtotal', label: 'Subtotal', value: 'Subtotal', role: 'label', max: 40 },
      { key: 'order.shipping', label: 'Shipping line', value: 'Shipping', role: 'label', max: 40 },
      { key: 'order.shipping_free', label: 'Shipping when free', value: 'Free', role: 'label', max: 40 },
      { key: 'order.tax', label: 'Tax line', value: 'Tax', role: 'label', max: 40 },
      { key: 'order.discount', label: 'Discount line', value: 'Discount', role: 'label', max: 40 },
      { key: 'order.total', label: 'Total', value: 'Total', role: 'label', max: 40 },
      { key: 'order.one_size', label: 'Item with no options', value: 'One size', role: 'label', max: 30 },
      {
        key: 'order.continue',
        label: 'Keep shopping button',
        value: 'Continue shopping',
        role: 'button',
        max: 40,
      },
      { key: 'order.my_orders', label: 'Order history link', value: 'My orders', role: 'button', max: 40 },
      { key: 'order.delivery', label: 'Delivery line', value: 'Delivery', role: 'label', max: 40 },
      {
        key: 'order.delivering_to',
        label: 'Address heading',
        value: 'Delivering to',
        role: 'heading',
        max: 60,
      },
      {
        key: 'order.open_whatsapp',
        label: 'Open WhatsApp button',
        value: 'Open WhatsApp',
        role: 'button',
        max: 40,
      },
      {
        key: 'order.unavailable',
        label: 'Order cannot be shown',
        value:
          'We cannot show this order here. Sign in to your account to see your order history, or check the confirmation email we sent when it was placed.',
        role: 'message',
        hint: 'Shown when the page is opened from a link outside the checkout.',
        multiline: true,
        max: 300,
      },
      // Push notifications: following the order from the confirmation page.
      {
        key: 'order.push_prompt',
        label: 'Order notifications offer',
        value: 'Get a notification on this device when your order moves.',
        role: 'message',
        hint: 'Shown on shops that offer push notifications, to browsers that support them.',
        max: 160,
      },
      { key: 'order.push_enable', label: 'Turn on notifications button', value: 'Notify me', role: 'button', max: 40 },
      {
        key: 'order.push_on',
        label: 'Notifications are on',
        value: "Notifications are on. We'll let you know when your order moves.",
        role: 'message',
        max: 160,
      },
      {
        key: 'order.push_disable',
        label: 'Turn off notifications button',
        value: 'Turn off',
        role: 'button',
        max: 40,
      },
      {
        key: 'order.push_blocked',
        label: 'Notifications are blocked',
        value: 'Notifications are blocked for this shop. Allow them in your browser settings to get order updates.',
        role: 'message',
        max: 200,
      },
      {
        key: 'order.push_failed',
        label: 'Notifications could not be set up',
        value: "We couldn't turn on notifications. Please try again.",
        role: 'message',
        max: 160,
      },
      // The pay page: an order placed in a WhatsApp chat, paid on the web.
      {
        key: 'order.pay_title',
        label: 'Pay page heading',
        value: 'Pay for order {number}',
        role: 'heading',
        hint: '{number} is the order number.',
        max: 80,
      },
      { key: 'order.pay_amount', label: 'Amount due label', value: 'Amount due', role: 'label', max: 40 },
      { key: 'order.pay_button', label: 'Pay button', value: 'Pay now', role: 'button', max: 40 },
      {
        key: 'order.pay_paid',
        label: 'Already paid',
        value: 'This order has already been paid. Thank you!',
        role: 'message',
        max: 160,
      },
      {
        key: 'order.pay_invalid',
        label: 'Link not valid',
        value: 'This payment link is not valid or has expired.',
        role: 'message',
        max: 160,
      },
      {
        key: 'order.pay_cancelled',
        label: 'Order cancelled',
        value: 'This order was cancelled, so there is nothing to pay.',
        role: 'message',
        max: 160,
      },
    ],
  },
  {
    id: 'order-outcome',
    area: 'checkout',
    label: 'What happened to the order',
    description: 'The sentence under the confirmation heading. {number} is drawn in bold.',
    fields: [
      {
        key: 'order.outcome_confirmed',
        label: 'Confirmed',
        value: 'Your order {number} for {amount} is confirmed. A confirmation is on its way to your email.',
        role: 'message',
        hint: '{number} is the order number, {amount} its total.',
        multiline: true,
        max: 300,
      },
      {
        key: 'order.outcome_confirmed_no_total',
        label: 'Confirmed, total unknown',
        value: 'Your order {number} is confirmed. A confirmation is on its way to your email.',
        role: 'message',
        multiline: true,
        max: 300,
      },
      {
        key: 'order.outcome_paid',
        label: 'Paid online',
        value:
          'We have received your payment for order {number} ({amount}). Your bank is confirming it now, and we will email you the moment it clears — usually within a minute.',
        role: 'message',
        hint: '{number} is the order number, {amount} its total.',
        multiline: true,
        max: 300,
      },
      {
        key: 'order.outcome_paid_no_total',
        label: 'Paid online, total unknown',
        value:
          'We have received your payment for order {number}. Your bank is confirming it now, and we will email you the moment it clears — usually within a minute.',
        role: 'message',
        multiline: true,
        max: 300,
      },
      {
        key: 'order.outcome_awaiting',
        label: 'Payment not finished',
        value:
          'Your order {number} for {amount} is saved but has not been paid for. We have emailed you a link to complete the payment; nothing is dispatched until it goes through.',
        role: 'message',
        hint: '{number} is the order number, {amount} its total.',
        multiline: true,
        max: 300,
      },
      {
        key: 'order.outcome_awaiting_no_total',
        label: 'Payment not finished, total unknown',
        value:
          'Your order {number} is saved but has not been paid for. We have emailed you a link to complete the payment; nothing is dispatched until it goes through.',
        role: 'message',
        multiline: true,
        max: 300,
      },
      {
        key: 'order.outcome_whatsapp',
        label: 'Ordered via WhatsApp',
        value:
          'Your order {number} for {amount} is saved. Send it to us on WhatsApp to confirm and arrange payment — tap the button below if WhatsApp did not open.',
        role: 'message',
        hint: '{number} is the order number, {amount} its total.',
        multiline: true,
        max: 300,
      },
      {
        key: 'order.outcome_whatsapp_no_total',
        label: 'Ordered via WhatsApp, total unknown',
        value:
          'Your order {number} is saved. Send it to us on WhatsApp to confirm and arrange payment — tap the button below if WhatsApp did not open.',
        role: 'message',
        multiline: true,
        max: 300,
      },
    ],
  },

  // --- Product cards and the product page's extras ------------------------
  {
    id: 'product-card',
    area: 'listing',
    label: 'Product cards',
    description: 'The badges on a product tile, wherever a grid or rail shows one.',
    fields: [
      {
        key: 'product_card.out_of_stock',
        label: 'Sold out badge',
        value: 'Out of stock',
        role: 'label',
        max: 30,
      },
      { key: 'product_card.available', label: 'In stock badge', value: 'Available', role: 'label', max: 30 },
      { key: 'product_card.sale', label: 'Sale badge', value: 'Sale', role: 'label', max: 20 },
      {
        key: 'product_card.colour_count_single',
        label: 'Colour count, one colour',
        value: '{count} colour',
        role: 'label',
        max: 30,
      },
      {
        key: 'product_card.colour_count',
        label: 'Colour count, several colours',
        value: '{count} colours',
        role: 'label',
        max: 30,
      },
      {
        key: 'product_card.new',
        label: 'New badge',
        value: 'New',
        role: 'label',
        hint: 'On products added recently, when the theme shows new badges.',
        max: 20,
      },
      {
        key: 'product_card.bestseller',
        label: 'Bestseller badge',
        value: 'Bestseller',
        role: 'label',
        hint: 'On your top-selling products, when the theme shows bestseller badges.',
        max: 20,
      },
      { key: 'product_card.quick_add', label: 'Quick add button', value: 'Add to cart', role: 'button', max: 30 },
      {
        key: 'product_card.added',
        label: 'Quick add, just added',
        value: 'Added',
        role: 'message',
        max: 30,
      },
      {
        key: 'product_card.choose_options',
        label: 'Quick add option picker title',
        value: 'Choose an option',
        role: 'heading',
        max: 40,
      },
      { key: 'product_card.close', label: 'Close option picker', value: 'Close', role: 'label', max: 20 },
    ],
  },
  {
    id: 'product-extras',
    area: 'product',
    label: 'Buying, saving and reviews',
    fields: [
      { key: 'product.buy_now', label: 'Buy now button', value: 'Buy now', role: 'button', max: 40 },
      { key: 'product.quantity', label: 'Quantity field', value: 'Quantity', role: 'label', max: 30 },
      { key: 'product.price', label: 'Price label', value: 'Price', role: 'label', max: 30 },
      {
        key: 'product.wishlist_add',
        label: 'Save to wishlist',
        value: 'Add to wishlist',
        role: 'button',
        max: 40,
      },
      {
        key: 'product.wishlist_remove',
        label: 'Remove from wishlist',
        value: 'Remove from wishlist',
        role: 'button',
        max: 40,
      },
      { key: 'product.compare', label: 'Compare button', value: 'Compare', role: 'button', max: 30 },
      { key: 'product.reviews', label: 'Reviews heading', value: 'Reviews', role: 'heading', max: 40 },
      { key: 'product.no_reviews', label: 'No reviews yet', value: 'No reviews yet.', role: 'empty', max: 120 },
      {
        key: 'product.write_review',
        label: 'Write a review button',
        value: 'Write a review',
        role: 'button',
        max: 40,
      },
      {
        key: 'product.related_title',
        label: 'Related products heading',
        value: 'You may also like',
        role: 'heading',
        max: 60,
      },
      {
        key: 'product.related_similar',
        label: 'Similar products heading',
        value: 'Similar products',
        role: 'heading',
        max: 60,
      },
      {
        key: 'product.related_also_liked',
        label: 'Also liked heading',
        value: 'Customers also liked',
        role: 'heading',
        max: 60,
      },
    ],
  },
  {
    id: 'product-reviews',
    area: 'product',
    label: 'Product reviews',
    description: 'The reviews section: its summary, its list and the form a signed-in customer writes in.',
    fields: [
      {
        key: 'reviews.summary_label',
        label: 'Rating read aloud',
        value: '{rating} out of 5 stars from {count} reviews',
        role: 'label',
        hint: 'Read aloud by screen readers. {rating} is the average, {count} how many reviews.',
        max: 80,
      },
      {
        key: 'reviews.based_on',
        label: 'Review count under the average',
        value: 'Based on {count} reviews',
        role: 'label',
        hint: '{count} is how many published reviews there are.',
        max: 60,
      },
      {
        key: 'reviews.based_on_single',
        label: 'Review count, just one',
        value: 'Based on 1 review',
        role: 'label',
        max: 60,
      },
      {
        key: 'reviews.stars',
        label: 'Star filter and bar',
        value: '{rating} stars',
        role: 'label',
        hint: '{rating} is a number from 1 to 5.',
        max: 30,
      },
      {
        key: 'reviews.filter_all',
        label: 'All ratings filter',
        value: 'All ratings',
        role: 'button',
        max: 30,
      },
      {
        key: 'reviews.filter_label',
        label: 'Rating filter',
        value: 'Filter by rating',
        role: 'label',
        max: 40,
      },
      {
        key: 'reviews.sort_label',
        label: 'Sort menu',
        value: 'Sort by',
        role: 'label',
        max: 30,
      },
      {
        key: 'reviews.sort_newest',
        label: 'Sort: newest',
        value: 'Newest',
        role: 'label',
        max: 30,
      },
      {
        key: 'reviews.sort_highest',
        label: 'Sort: highest',
        value: 'Highest rated',
        role: 'label',
        max: 30,
      },
      {
        key: 'reviews.sort_lowest',
        label: 'Sort: lowest',
        value: 'Lowest rated',
        role: 'label',
        max: 30,
      },
      {
        key: 'reviews.sort_photos',
        label: 'Sort: with photos',
        value: 'With photos',
        role: 'label',
        max: 30,
      },
      {
        key: 'reviews.verified',
        label: 'Verified purchase badge',
        value: 'Verified purchase',
        role: 'label',
        max: 30,
      },
      {
        key: 'reviews.reply',
        label: 'Shop reply heading',
        value: 'Reply from {store}',
        role: 'label',
        hint: '{store} is your shop’s name.',
        max: 60,
      },
      {
        key: 'reviews.photo_alt',
        label: 'Review photo',
        value: 'Photo from {author}',
        role: 'label',
        hint: 'Read aloud by screen readers. {author} is the reviewer.',
        max: 60,
      },
      {
        key: 'reviews.load_more',
        label: 'Load more button',
        value: 'Load more reviews',
        role: 'button',
        max: 40,
      },
      {
        key: 'reviews.empty',
        label: 'No reviews yet',
        value: 'Be the first to review this product',
        role: 'empty',
        max: 120,
      },
      {
        key: 'reviews.empty_filtered',
        label: 'No reviews at that rating',
        value: 'No reviews with this rating yet.',
        role: 'empty',
        max: 120,
      },
      {
        key: 'reviews.load_error',
        label: 'Reviews would not load',
        value: 'Reviews could not be loaded. Please try again.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'reviews.login',
        label: 'Sign in to review',
        value: 'Log in to write a review',
        role: 'button',
        max: 60,
      },
      {
        key: 'reviews.your_rating',
        label: 'Rating question',
        value: 'Your rating',
        role: 'label',
        max: 40,
      },
      {
        key: 'reviews.title_label',
        label: 'Review title field',
        value: 'Title (optional)',
        role: 'label',
        max: 40,
      },
      {
        key: 'reviews.body_label',
        label: 'Review text field',
        value: 'Your review',
        role: 'label',
        max: 40,
      },
      {
        key: 'reviews.submit',
        label: 'Send review button',
        value: 'Submit review',
        role: 'button',
        max: 40,
      },
      {
        key: 'reviews.save',
        label: 'Save edited review',
        value: 'Save changes',
        role: 'button',
        max: 40,
      },
      {
        key: 'reviews.cancel',
        label: 'Cancel editing',
        value: 'Cancel',
        role: 'button',
        max: 30,
      },
      {
        key: 'reviews.edit',
        label: 'Edit review button',
        value: 'Edit',
        role: 'button',
        max: 30,
      },
      {
        key: 'reviews.yours',
        label: 'Their own review heading',
        value: 'Your review',
        role: 'heading',
        max: 40,
      },
      {
        key: 'reviews.status_pending',
        label: 'Waiting for approval',
        value: 'Awaiting approval',
        role: 'label',
        max: 40,
      },
      {
        key: 'reviews.status_published',
        label: 'Review is live',
        value: 'Published',
        role: 'label',
        max: 40,
      },
      {
        key: 'reviews.status_hidden',
        label: 'Review was hidden',
        value: 'Not shown in the shop',
        role: 'label',
        max: 40,
      },
      {
        key: 'reviews.thanks',
        label: 'After sending',
        value: 'Thanks for your review!',
        role: 'message',
        max: 120,
      },
      {
        key: 'reviews.exists',
        label: 'Already reviewed',
        value: 'You have already reviewed this product. You can edit your review instead.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'reviews.error',
        label: 'Review failed',
        value: 'That didn’t go through. Please try again.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'reviews.rating_required',
        label: 'No stars chosen',
        value: 'Choose a star rating.',
        role: 'validation',
        max: 80,
      },
      {
        key: 'reviews.body_required',
        label: 'No review text',
        value: 'Tell other shoppers what you thought.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'reviews.sending',
        label: 'While sending',
        value: 'Sending…',
        role: 'message',
        max: 30,
      },
      {
        key: 'reviews.photos_label',
        label: 'Photo field',
        value: 'Photos (optional, up to 4)',
        role: 'label',
        max: 60,
      },
      {
        key: 'reviews.add_photo',
        label: 'Add a photo button',
        value: 'Add a photo',
        role: 'button',
        max: 30,
      },
      {
        key: 'reviews.remove_photo',
        label: 'Remove a photo',
        value: 'Remove photo {number}',
        role: 'button',
        hint: 'Read aloud by screen readers. {number} is which photo.',
        max: 40,
      },
      {
        key: 'reviews.uploading',
        label: 'While a photo uploads',
        value: 'Uploading…',
        role: 'message',
        max: 30,
      },
      {
        key: 'reviews.photo_error',
        label: 'Photo refused',
        value: 'That photo could not be added. Try a JPG, PNG or WebP image.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'reviews.photo_too_large',
        label: 'Photo too large',
        value: 'That photo is too large. Please choose a smaller one.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'reviews.too_long',
        label: 'Review too long',
        value: 'Your review is too long. Please shorten it and try again.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'reviews.rate_limited',
        label: 'Too many reviews',
        value: 'You have sent a lot just now. Please wait a while and try again.',
        role: 'validation',
        max: 160,
      },
    ],
  },
  {
    id: 'product-page-parts',
    area: 'product',
    label: 'Product page extras',
    fields: [
      {
        key: 'product_page.save_percent',
        label: 'Saving badge',
        value: 'Save {percent}%',
        role: 'promo',
        hint: '{percent} is the discount off the original price.',
        max: 30,
      },
      { key: 'product_page.was', label: 'Original price', value: 'Was', role: 'label', max: 20 },
      { key: 'product_page.sku', label: 'SKU label', value: 'SKU', role: 'label', max: 20 },
      { key: 'product_page.in_stock', label: 'In stock', value: 'In stock', role: 'label', max: 40 },
      {
        key: 'product_page.quantity_decrease',
        label: 'Quantity down',
        value: 'One fewer',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 30,
      },
      {
        key: 'product_page.quantity_increase',
        label: 'Quantity up',
        value: 'One more',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 30,
      },
      {
        key: 'product_page.delivery_returns',
        label: 'Delivery and returns heading',
        value: 'Delivery and returns',
        role: 'heading',
        max: 60,
      },
      {
        key: 'product_page.breadcrumb',
        label: 'Breadcrumb',
        value: 'Breadcrumb',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 40,
      },
      { key: 'product_page.breadcrumb_home', label: 'Breadcrumb start', value: 'Home', role: 'label', max: 30 },
      {
        key: 'product_page.back',
        label: 'Back link',
        value: 'Back',
        role: 'label',
        hint: 'Stands in for the breadcrumb on a phone while the app bar is on.',
        max: 30,
      },
      {
        key: 'product_page.payment_security',
        label: 'Payment and security heading',
        value: 'Payment & security',
        role: 'heading',
        max: 60,
      },
      { key: 'product_page.share', label: 'Share heading', value: 'Share', role: 'label', max: 30 },
      {
        key: 'product_page.share_facebook',
        label: 'Share on Facebook',
        value: 'Share on Facebook',
        role: 'label',
        max: 40,
      },
      { key: 'product_page.share_x', label: 'Share on X', value: 'Share on X', role: 'label', max: 40 },
      {
        key: 'product_page.open_page',
        label: 'Open the page link',
        value: 'Open this page on its own',
        role: 'label',
        max: 60,
      },
      {
        key: 'product_page.share_whatsapp',
        label: 'Share on WhatsApp',
        value: 'Share on WhatsApp',
        role: 'label',
        max: 40,
      },
      {
        key: 'product_page.buy_on_whatsapp',
        label: 'Buy on WhatsApp button',
        value: 'Buy on WhatsApp',
        role: 'button',
        hint: 'Opens a chat with you about the product being looked at. Shown once WhatsApp is connected.',
        max: 40,
      },
      {
        key: 'product_page.compare_title',
        label: 'Comparison heading',
        value: 'Compare with similar',
        role: 'heading',
        max: 60,
      },
      {
        key: 'product_page.compare_caption',
        label: 'Comparison table name',
        value: '{product} compared with similar products',
        role: 'label',
        hint: 'Read aloud by screen readers. {product} is the product name.',
        max: 120,
      },
      { key: 'product_page.compare_summary', label: 'Summary row', value: 'Summary', role: 'label', max: 30 },
      { key: 'product_page.availability', label: 'Availability row', value: 'Availability', role: 'label', max: 30 },
      {
        key: 'product_page.delivery_check',
        label: 'Delivery check heading',
        value: 'Check delivery',
        role: 'heading',
        max: 60,
      },
      {
        key: 'product_page.pincode_placeholder',
        label: 'PIN code box hint',
        value: 'Enter PIN code',
        role: 'placeholder',
        max: 40,
      },
      { key: 'product_page.pincode_check', label: 'Check button', value: 'Check', role: 'button', max: 20 },
      {
        key: 'product_page.pincode_invalid',
        label: 'PIN code not valid',
        value: 'Enter a 6-digit PIN code.',
        role: 'validation',
        max: 80,
      },
      {
        key: 'product_page.delivered_by',
        label: 'Delivery date',
        value: 'Delivered by {date}',
        role: 'label',
        hint: '{date} is replaced by the expected day, like Thu, 9 Oct.',
        max: 60,
      },
      {
        key: 'product_page.delivered_in',
        label: 'Delivery time range',
        value: 'Usually delivered in {min}-{max} days',
        role: 'label',
        hint: '{min} and {max} are the days set on the delivery check component.',
        max: 80,
      },
      {
        key: 'product_page.delivering_to',
        label: 'Checked PIN code',
        value: 'Delivering to {pincode}',
        role: 'label',
        hint: '{pincode} is the PIN code the customer typed.',
        max: 60,
      },
      { key: 'product_page.pincode_change', label: 'Change PIN code', value: 'Change', role: 'button', max: 20 },
      {
        key: 'product_page.cod_available',
        label: 'Cash on delivery offered',
        value: 'Cash on delivery available',
        role: 'label',
        max: 80,
      },
      {
        key: 'product_page.stock_left',
        label: 'Low stock',
        value: 'Only {count} left',
        role: 'promo',
        hint: '{count} is how many pieces are left.',
        max: 40,
      },
      { key: 'product_page.offers', label: 'Offers heading', value: 'Offers', role: 'heading', max: 40 },
      { key: 'product_page.copy_code', label: 'Copy code button', value: 'Copy', role: 'button', max: 20 },
      { key: 'product_page.seller_heading', label: 'Seller information heading', value: 'Seller information', role: 'heading', max: 60 },
      { key: 'product_page.seller_name', label: 'Seller name label', value: 'Sold by', role: 'label', max: 40 },
      { key: 'product_page.seller_address', label: 'Seller address label', value: 'Address', role: 'label', max: 40 },
      { key: 'product_page.seller_gstin', label: 'Seller GSTIN label', value: 'GSTIN', role: 'label', max: 40 },
      { key: 'product_page.seller_fssai', label: 'Seller FSSAI label', value: 'FSSAI licence', role: 'label', max: 40 },
      { key: 'product_page.seller_email', label: 'Seller email label', value: 'Customer care email', role: 'label', max: 40 },
      { key: 'product_page.seller_phone', label: 'Seller phone label', value: 'Customer care phone', role: 'label', max: 40 },
      { key: 'product_page.code_copied', label: 'Code copied', value: 'Copied', role: 'label', max: 20 },
      { key: 'product_page.size_chart', label: 'Size chart link', value: 'Size chart', role: 'button', max: 40 },
      {
        key: 'product_page.bundle_heading',
        label: 'Bought-together heading',
        value: 'Frequently bought together',
        role: 'heading',
        max: 60,
      },
      { key: 'product_page.bundle_this_item', label: 'Bought-together: this product', value: 'This item', role: 'label', max: 30 },
      { key: 'product_page.bundle_total', label: 'Bought-together total', value: 'Total price', role: 'label', max: 30 },
      {
        key: 'product_page.bundle_save',
        label: 'Bought-together saving',
        value: 'You save {amount}',
        role: 'promo',
        hint: '{amount} is the bundle discount, in rupees.',
        max: 40,
      },
      { key: 'product_page.bundle_add_all', label: 'Add all button', value: 'Add all to cart', role: 'button', max: 30 },
      {
        key: 'product_page.bundle_add_selected',
        label: 'Add selected button',
        value: 'Add selected to cart',
        role: 'button',
        max: 30,
      },
      {
        key: 'product_page.bundle_unlock',
        label: 'Bought-together: tick all',
        value: 'Add all {count} to get the bundle discount',
        role: 'label',
        hint: '{count} is how many items are in the set.',
        max: 80,
      },
      { key: 'product_page.bundle_added', label: 'Bought-together added', value: 'Added to cart', role: 'label', max: 30 },
    ],
  },
  {
    id: 'product-questions',
    area: 'product',
    label: 'Questions and back-in-stock',
    description: 'The questions-and-answers component, and the "Notify me" box shown when an option sells out.',
    fields: [
      { key: 'product_qa.heading', label: 'Questions heading', value: 'Questions and answers', role: 'heading', max: 60 },
      {
        key: 'product_qa.empty',
        label: 'No questions yet',
        value: 'No questions yet. Be the first to ask.',
        role: 'empty',
        max: 100,
      },
      { key: 'product_qa.ask_button', label: 'Ask button', value: 'Ask a question', role: 'button', max: 30 },
      { key: 'product_qa.answer_from', label: 'Answer label', value: 'Answer from the store', role: 'label', max: 40 },
      { key: 'product_qa.awaiting', label: 'Not answered yet', value: 'Waiting for an answer', role: 'label', max: 40 },
      {
        key: 'product_qa.asked_by',
        label: 'Who asked',
        value: 'Asked by {name}',
        role: 'label',
        hint: '{name} is the first name and initial of the customer who asked.',
        max: 40,
      },
      { key: 'product_qa.more', label: 'Show more button', value: 'Show more questions', role: 'button', max: 30 },
      { key: 'product_qa.name', label: 'Name box', value: 'Your name', role: 'label', max: 30 },
      { key: 'product_qa.email', label: 'Email box', value: 'Email (to get the answer)', role: 'label', max: 40 },
      { key: 'product_qa.question', label: 'Question box', value: 'Your question', role: 'label', max: 30 },
      {
        key: 'product_qa.question_placeholder',
        label: 'Question box hint',
        value: 'What would you like to know?',
        role: 'placeholder',
        max: 60,
      },
      { key: 'product_qa.submit', label: 'Send button', value: 'Send question', role: 'button', max: 30 },
      { key: 'product_qa.cancel', label: 'Cancel button', value: 'Cancel', role: 'button', max: 20 },
      {
        key: 'product_qa.sent',
        label: 'Question sent',
        value: 'Thanks! Your question has been sent. The store will answer it soon.',
        role: 'message',
        max: 120,
      },
      {
        key: 'product_qa.error',
        label: 'Question not sent',
        value: 'Your question could not be sent. Please try again.',
        role: 'message',
        max: 100,
      },
      { key: 'product_qa.load_error', label: 'Questions not loaded', value: 'Questions could not be loaded.', role: 'message', max: 80 },
      { key: 'product_qa.name_required', label: 'Name missing', value: 'Enter your name.', role: 'validation', max: 60 },
      { key: 'product_qa.question_required', label: 'Question missing', value: 'Type your question.', role: 'validation', max: 60 },
      { key: 'notify_me.heading', label: 'Sold-out heading', value: 'Sold out', role: 'heading', max: 40 },
      {
        key: 'notify_me.prompt',
        label: 'Notify me line',
        value: 'Get an email when this is back in stock.',
        role: 'label',
        max: 80,
      },
      { key: 'notify_me.email_placeholder', label: 'Email box hint', value: 'Your email', role: 'placeholder', max: 40 },
      { key: 'notify_me.submit', label: 'Notify me button', value: 'Notify me', role: 'button', max: 30 },
      {
        key: 'notify_me.done',
        label: 'Signed up',
        value: "Done! We'll email you when it's back.",
        role: 'message',
        max: 80,
      },
      {
        key: 'notify_me.error',
        label: 'Sign-up failed',
        value: 'Could not sign you up. Please try again.',
        role: 'message',
        max: 80,
      },
      {
        key: 'notify_me.push_too',
        label: 'Also notify on this device',
        value: 'Also notify me on this device',
        role: 'label',
        hint: 'A tick box, shown on shops that offer push notifications.',
        max: 60,
      },
      {
        key: 'notify_me.invalid_email',
        label: 'Email not valid',
        value: 'Enter a valid email address.',
        role: 'validation',
        max: 60,
      },
    ],
  },
  {
    id: 'product-zoom',
    area: 'product',
    label: 'Zoomed picture',
    fields: [
      {
        key: 'product_page.zoomed',
        label: 'Zoom dialog name',
        value: '{product}, zoomed',
        role: 'label',
        hint: 'Read aloud by screen readers. {product} is the product name.',
        max: 120,
      },
      { key: 'product_page.zoom_in', label: 'Zoom in', value: 'Zoom in', role: 'label', max: 30 },
      { key: 'product_page.zoom_out', label: 'Zoom out', value: 'Zoom out', role: 'label', max: 30 },
      { key: 'product_page.zoom_reset', label: 'Reset zoom', value: 'Reset zoom', role: 'label', max: 30 },
      {
        key: 'product_page.previous_image',
        label: 'Previous picture',
        value: 'Previous image',
        role: 'label',
        max: 40,
      },
      { key: 'product_page.next_image', label: 'Next picture', value: 'Next image', role: 'label', max: 40 },
    ],
  },

  // --- Filters ------------------------------------------------------------
  {
    id: 'search-facets',
    area: 'search',
    label: 'Filter panel',
    fields: [
      { key: 'search.price', label: 'Price filter', value: 'Price', role: 'label', max: 30 },
      { key: 'search.price_min', label: 'Lowest price hint', value: 'Min', role: 'placeholder', max: 20 },
      { key: 'search.price_max', label: 'Highest price hint', value: 'Max', role: 'placeholder', max: 20 },
      { key: 'search.in_stock', label: 'In stock filter', value: 'In stock only', role: 'label', max: 40 },
      { key: 'search.category', label: 'Category filter', value: 'Category', role: 'label', max: 30 },
      { key: 'search.brand', label: 'Brand filter', value: 'Brand', role: 'label', max: 30 },
      { key: 'search.type', label: 'Type filter', value: 'Type', role: 'label', max: 30 },
      { key: 'search.tags', label: 'Tags filter', value: 'Tags', role: 'label', max: 30 },
      {
        key: 'search.show_all',
        label: 'Show every value',
        value: 'Show all {count}',
        role: 'button',
        hint: '{count} is replaced by how many values there are.',
        max: 40,
      },
      { key: 'search.show_fewer', label: 'Show fewer values', value: 'Show fewer', role: 'button', max: 40 },
      {
        key: 'search.no_products',
        label: 'Nothing found',
        value: 'No products found',
        role: 'empty',
        max: 120,
      },
      {
        key: 'search.price_lowest',
        label: 'Lowest price field',
        value: 'Lowest price',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 40,
      },
      {
        key: 'search.price_highest',
        label: 'Highest price field',
        value: 'Highest price',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 40,
      },
      {
        key: 'search.price_apply',
        label: 'Apply the price range',
        value: 'Apply price range',
        role: 'button',
        hint: 'Read aloud by screen readers.',
        max: 40,
      },
      {
        key: 'search.price_any',
        label: 'Open-ended price',
        value: 'Any',
        role: 'label',
        hint: 'Stands in for a missing end of the price range on its filter chip.',
        max: 20,
      },
      { key: 'search.in_stock_chip', label: 'In stock chip', value: 'In stock', role: 'label', max: 30 },
    ],
  },
  {
    id: 'search-sort',
    area: 'search',
    label: 'Sorting',
    fields: [
      { key: 'search.sort_newest', label: 'Newest first', value: 'Newest first', role: 'label', max: 40 },
      { key: 'search.sort_oldest', label: 'Oldest first', value: 'Oldest first', role: 'label', max: 40 },
      {
        key: 'search.sort_price_asc',
        label: 'Cheapest first',
        value: 'Price: low to high',
        role: 'label',
        max: 40,
      },
      {
        key: 'search.sort_price_desc',
        label: 'Dearest first',
        value: 'Price: high to low',
        role: 'label',
        max: 40,
      },
      { key: 'search.sort_name_asc', label: 'Name A to Z', value: 'Name: A to Z', role: 'label', max: 40 },
      { key: 'search.sort_name_desc', label: 'Name Z to A', value: 'Name: Z to A', role: 'label', max: 40 },
      {
        key: 'search.meta_title_term',
        label: 'Browser tab title',
        value: 'Search: {term}',
        role: 'label',
        hint: '{term} is replaced by what the shopper typed.',
        max: 60,
      },
    ],
  },

  // --- The shopper's account ----------------------------------------------
  {
    id: 'account-nav',
    area: 'account',
    label: 'Account menu',
    fields: [
      { key: 'account.title', label: 'Heading', value: 'My account', role: 'heading', max: 60 },
      { key: 'account.overview', label: 'Overview tab', value: 'Overview', role: 'label', max: 30 },
      { key: 'account.orders', label: 'Orders tab', value: 'Orders', role: 'label', max: 30 },
      { key: 'account.bookings', label: 'Bookings tab', value: 'Bookings', role: 'label', max: 30 },
      { key: 'account.addresses', label: 'Addresses tab', value: 'Addresses', role: 'label', max: 30 },
      { key: 'account.wishlist', label: 'Wishlist tab', value: 'Wishlist', role: 'label', max: 30 },
      { key: 'account.profile', label: 'Profile tab', value: 'Profile', role: 'label', max: 30 },
      { key: 'account.sign_out', label: 'Sign out', value: 'Sign out', role: 'button', max: 30 },
      {
        key: 'account.signed_out',
        label: 'After signing out',
        value: 'You have been signed out.',
        role: 'message',
        hint: 'Shown on the home page once a shopper signs out.',
        max: 80,
      },
      { key: 'account.loading', label: 'While it loads', value: 'Loading…', role: 'message', max: 40 },
    ],
  },
  {
    id: 'account-details',
    area: 'account',
    label: 'Profile and orders',
    fields: [
      { key: 'account.language', label: 'Language section', value: 'Language', role: 'heading', max: 40 },
      {
        key: 'account.preferred_language',
        label: 'Preferred language field',
        value: 'Preferred language',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.preferred_language_hint',
        label: 'Preferred language hint',
        value: 'We will show the shop and send your emails in this language.',
        role: 'message',
        max: 160,
      },
      { key: 'account.save_changes', label: 'Save button', value: 'Save changes', role: 'button', max: 40 },
      { key: 'account.saving', label: 'Save button, working', value: 'Saving…', role: 'button', max: 40 },
      { key: 'account.saved', label: 'Saved', value: 'Details saved.', role: 'message', max: 80 },
      {
        key: 'account.passwords_mismatch',
        label: 'Passwords differ',
        value: 'Both passwords must match.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'account.order_details',
        label: 'Order details heading',
        value: 'Order details',
        role: 'heading',
        max: 60,
      },
      {
        key: 'account.no_orders',
        label: 'No orders yet',
        value: 'You have not placed an order yet.',
        role: 'empty',
        max: 160,
      },
      {
        key: 'account.no_addresses',
        label: 'No addresses yet',
        value: 'No addresses yet.',
        role: 'empty',
        max: 160,
      },
      { key: 'account.subtotal', label: 'Subtotal', value: 'Subtotal', role: 'label', max: 40 },
      { key: 'account.total', label: 'Total', value: 'Total', role: 'label', max: 40 },
    ],
  },
  {
    id: 'account-overview',
    area: 'account',
    label: 'Account overview',
    fields: [
      {
        key: 'account.hello',
        label: 'Greeting',
        value: 'Hello, {name}',
        role: 'heading',
        hint: '{name} is the shopper’s first name.',
        max: 60,
      },
      {
        key: 'account.orders_spent_one',
        label: 'One order so far',
        value: '{count} order · {amount} spent with us.',
        role: 'message',
        hint: '{count} is the number of orders, {amount} what they add up to.',
        max: 120,
      },
      {
        key: 'account.orders_spent_many',
        label: 'Several orders so far',
        value: '{count} orders · {amount} spent with us.',
        role: 'message',
        max: 120,
      },
      { key: 'account.recent_orders', label: 'Recent orders heading', value: 'Recent orders', role: 'heading', max: 60 },
      { key: 'account.see_all', label: 'See all link', value: 'See all', role: 'button', max: 30 },
      { key: 'account.nothing_yet', label: 'No orders, heading', value: 'Nothing here yet', role: 'empty', max: 60 },
      {
        key: 'account.nothing_yet_body',
        label: 'No orders, line',
        value: 'Your orders will appear here once you have placed one.',
        role: 'empty',
        max: 160,
      },
      { key: 'account.start_shopping', label: 'Start shopping button', value: 'Start shopping', role: 'button', max: 40 },
      {
        key: 'account.order_line_one',
        label: 'Order row, one item',
        value: '{date} · {count} item · {total}',
        role: 'label',
        hint: '{date} is when it was placed, {count} how many items, {total} what it cost.',
        max: 80,
      },
      {
        key: 'account.order_line_many',
        label: 'Order row, several items',
        value: '{date} · {count} items · {total}',
        role: 'label',
        max: 80,
      },
      {
        key: 'account.default_address',
        label: 'Default address heading',
        value: 'Default delivery address',
        role: 'heading',
        max: 60,
      },
      { key: 'account.manage', label: 'Manage addresses link', value: 'Manage', role: 'button', max: 30 },
      {
        key: 'account.no_default_address',
        label: 'No address saved',
        value: 'No address saved yet. Add one and checkout will fill it in for you.',
        role: 'empty',
        max: 160,
      },
      {
        key: 'account.account_loading',
        label: 'While the account loads',
        value: 'Loading your account…',
        role: 'message',
        max: 60,
      },
    ],
  },
  {
    id: 'account-orders',
    area: 'account',
    label: 'Orders and one order',
    fields: [
      {
        key: 'account.orders_loading',
        label: 'While orders load',
        value: 'Loading your orders…',
        role: 'message',
        max: 60,
      },
      { key: 'account.no_orders_title', label: 'No orders, heading', value: 'No orders yet', role: 'empty', max: 60 },
      {
        key: 'account.no_orders_body',
        label: 'No orders, line',
        value: 'Once you have bought something, it will show up here with its delivery status.',
        role: 'empty',
        max: 160,
      },
      { key: 'account.previous', label: 'Previous page', value: 'Previous', role: 'button', max: 30 },
      { key: 'account.next', label: 'Next page', value: 'Next', role: 'button', max: 30 },
      { key: 'account.invoice', label: 'Invoice button, in the list', value: 'Invoice', role: 'button', max: 20 },
      {
        key: 'account.download_invoice',
        label: 'Download invoice button',
        value: 'Download invoice',
        role: 'button',
        max: 40,
      },
      {
        key: 'account.downloading_invoice',
        label: 'While the invoice downloads',
        value: 'Preparing invoice…',
        role: 'message',
        max: 40,
      },
      {
        key: 'account.invoice_error',
        label: 'Invoice download failed',
        value: 'We could not download the invoice.',
        role: 'validation',
        max: 160,
      },
      { key: 'account.order_loading', label: 'While an order loads', value: 'Loading order…', role: 'message', max: 60 },
      { key: 'account.order_not_found', label: 'Unknown order', value: 'Order not found', role: 'heading', max: 60 },
      {
        key: 'account.order_not_found_body',
        label: 'Unknown order, line',
        value: 'We could not find that order on your account.',
        role: 'empty',
        max: 160,
      },
      {
        key: 'account.back_to_orders',
        label: 'Back to orders link',
        value: 'Back to your orders',
        role: 'button',
        max: 40,
      },
      { key: 'account.all_orders', label: 'All orders link', value: 'All orders', role: 'button', max: 40 },
      {
        key: 'account.placed_on',
        label: 'When it was placed',
        value: 'Placed {date}',
        role: 'label',
        hint: '{date} is the date and time of the order.',
        max: 60,
      },
      {
        key: 'account.cancel_error',
        label: 'Cancelling failed',
        value: 'We could not cancel this order.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'account.reorder_error',
        label: 'Buying again failed',
        value: 'We could not rebuild that cart.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'account.reorder_none',
        label: 'Nothing left to buy again',
        value: 'None of these are available to buy right now.',
        role: 'message',
        max: 160,
      },
      {
        key: 'account.reorder_partial_one',
        label: 'Bought again in part, one item',
        value: 'Added {count} item. No longer available: {items}.',
        role: 'message',
        hint: '{count} is how many were added, {items} the names that were not.',
        max: 200,
      },
      {
        key: 'account.reorder_partial_many',
        label: 'Bought again in part, several items',
        value: 'Added {count} items. No longer available: {items}.',
        role: 'message',
        max: 200,
      },
      { key: 'account.tracking', label: 'Tracking heading', value: 'Tracking', role: 'heading', max: 40 },
      {
        key: 'account.courier',
        label: 'Unnamed courier',
        value: 'Courier',
        role: 'label',
        hint: 'Used when the shipment does not name its courier.',
        max: 40,
      },
      {
        key: 'account.expected_by',
        label: 'Expected delivery',
        value: 'Expected by {date}',
        role: 'label',
        hint: '{date} is the courier’s estimate.',
        max: 60,
      },
      { key: 'account.track_parcel', label: 'Tracking link', value: 'Track this parcel', role: 'button', max: 40 },
      {
        key: 'account.tracking_history',
        label: 'Tracking history',
        value: 'Tracking history',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 60,
      },
      { key: 'account.items', label: 'Items heading', value: 'Items', role: 'heading', max: 40 },
      {
        key: 'account.item_quantity',
        label: 'Quantity of an item',
        value: 'Qty {quantity}',
        role: 'label',
        hint: '{quantity} is how many were bought.',
        max: 30,
      },
      { key: 'account.progress', label: 'Progress heading', value: 'Progress', role: 'heading', max: 40 },
      { key: 'account.summary', label: 'Summary heading', value: 'Summary', role: 'heading', max: 40 },
      { key: 'account.discount', label: 'Discount line', value: 'Discount', role: 'label', max: 40 },
      { key: 'account.delivery', label: 'Delivery line', value: 'Delivery', role: 'label', max: 40 },
      { key: 'account.cod_fee', label: 'Cash on delivery fee line', value: 'Cash on delivery fee', role: 'label', max: 40 },
      { key: 'account.tax', label: 'Tax line', value: 'GST included', role: 'label', max: 40 },
      { key: 'account.gift_card', label: 'Gift card line', value: 'Gift card', role: 'label', max: 40 },
      { key: 'account.paid', label: 'Amount paid', value: 'Paid', role: 'label', max: 40 },
      { key: 'account.delivering_to', label: 'Address heading', value: 'Delivering to', role: 'heading', max: 60 },
      {
        key: 'account.adding_to_cart',
        label: 'Buy again button, working',
        value: 'Adding to cart…',
        role: 'button',
        max: 40,
      },
      { key: 'account.buy_these_again', label: 'Buy again button', value: 'Buy these again', role: 'button', max: 40 },
      {
        key: 'account.cancel_order',
        label: 'Cancel order button',
        value: 'Cancel this order',
        role: 'button',
        max: 40,
      },
      {
        key: 'account.cancel_confirm',
        label: 'Cancel, the question',
        value: 'Cancel this order?',
        role: 'heading',
        max: 60,
      },
      {
        key: 'account.cancel_body',
        label: 'Cancel, what happens',
        value: 'Anything you paid is refunded, and the items go back on sale.',
        role: 'message',
        max: 160,
      },
      { key: 'account.cancelling', label: 'Cancel button, working', value: 'Cancelling…', role: 'button', max: 40 },
      { key: 'account.cancel_yes', label: 'Confirm cancelling', value: 'Yes, cancel it', role: 'button', max: 40 },
      { key: 'account.cancel_keep', label: 'Keep the order', value: 'Keep the order', role: 'button', max: 40 },
      {
        key: 'account.cancel_reason',
        label: 'Cancel, the reason',
        value: 'Why are you cancelling?',
        role: 'label',
        max: 60,
      },
      {
        key: 'account.reason_pick',
        label: 'Reason, not chosen yet',
        value: 'Choose a reason',
        role: 'placeholder',
        max: 40,
      },
      {
        key: 'account.reason_other',
        label: 'Reason, none of the above',
        value: 'Other',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.cancel_comment',
        label: 'Cancel, more detail',
        value: 'Anything else the store should know? (optional)',
        role: 'label',
        max: 80,
      },
      {
        key: 'account.cancel_comment_required',
        label: 'Reason, in your own words',
        value: 'Tell the store why',
        role: 'label',
        max: 60,
      },
      {
        key: 'account.cancelled_by_customer',
        label: 'Cancelled by the shopper',
        value: 'You cancelled this order: {reason}',
        role: 'message',
        max: 120,
      },
      {
        key: 'account.cancelled_by_merchant',
        label: 'Cancelled by the store',
        value: 'The store cancelled this order: {reason}',
        role: 'message',
        max: 120,
      },
      {
        key: 'account.cancelled_by_system',
        label: 'Cancelled automatically',
        value: 'This order was cancelled: {reason}',
        role: 'message',
        max: 120,
      },
      {
        key: 'account.request_start',
        label: 'Return or replace button',
        value: 'Return, refund or replace',
        role: 'button',
        max: 40,
      },
      {
        key: 'account.request_cancel_items',
        label: 'Cancel some items button',
        value: 'Cancel some items',
        role: 'button',
        max: 40,
      },
      {
        key: 'account.request_deadline',
        label: 'Return window, the last day',
        value: 'You can ask until {date}.',
        role: 'message',
        max: 80,
      },
      {
        key: 'account.request_title',
        label: 'Request form heading',
        value: 'What would you like to do?',
        role: 'heading',
        max: 60,
      },
      {
        key: 'account.request_kind',
        label: 'Request type question',
        value: 'Choose one',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.request_kind_cancellation',
        label: 'Request type: cancel items',
        value: 'Cancel items',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.request_kind_return',
        label: 'Request type: return',
        value: 'Return',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.request_kind_refund',
        label: 'Request type: refund',
        value: 'Refund',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.request_kind_replacement',
        label: 'Request type: replacement',
        value: 'Replacement',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.request_items',
        label: 'Which items question',
        value: 'Which items?',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.request_quantity',
        label: 'Quantity of one item',
        value: 'Quantity of {product}',
        role: 'label',
        max: 80,
      },
      {
        key: 'account.request_reason',
        label: 'Request, the reason',
        value: 'Reason',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.request_comment',
        label: 'Request, more detail',
        value: 'Details (optional)',
        role: 'label',
        max: 60,
      },
      {
        key: 'account.request_photos',
        label: 'Request, photos',
        value: 'Photos (optional)',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.request_photo_add',
        label: 'Add a photo',
        value: 'Add a photo',
        role: 'button',
        max: 40,
      },
      {
        key: 'account.request_photo_remove',
        label: 'Remove a photo',
        value: 'Remove photo',
        role: 'button',
        max: 40,
      },
      {
        key: 'account.request_photo_uploading',
        label: 'Photo uploading',
        value: 'Uploading…',
        role: 'message',
        max: 40,
      },
      {
        key: 'account.request_photo_error',
        label: 'Photo failed',
        value: 'Could not upload that photo.',
        role: 'message',
        max: 80,
      },
      {
        key: 'account.request_submit',
        label: 'Send request button',
        value: 'Send request',
        role: 'button',
        max: 40,
      },
      {
        key: 'account.request_sending',
        label: 'Send request, working',
        value: 'Sending…',
        role: 'button',
        max: 40,
      },
      {
        key: 'account.request_close',
        label: 'Close the request form',
        value: 'Never mind',
        role: 'button',
        max: 40,
      },
      {
        key: 'account.request_sent',
        label: 'Request sent',
        value: 'Request sent. The store will get back to you.',
        role: 'message',
        max: 120,
      },
      {
        key: 'account.request_error',
        label: 'Request failed',
        value: 'Could not send the request. Please try again.',
        role: 'message',
        max: 120,
      },
      {
        key: 'account.requests_title',
        label: 'Requests heading',
        value: 'Your requests',
        role: 'heading',
        max: 60,
      },
      {
        key: 'account.request_status_requested',
        label: 'Request waiting',
        value: 'Waiting for the store',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.request_status_approved',
        label: 'Request approved',
        value: 'Approved',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.request_status_rejected',
        label: 'Request declined',
        value: 'Declined',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.request_status_completed',
        label: 'Request done',
        value: 'Completed',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.request_reason_line',
        label: 'Request, your reason',
        value: 'Reason: {reason}',
        role: 'label',
        max: 80,
      },
      {
        key: 'account.request_store_reply',
        label: 'Request, the store\'s answer',
        value: 'Store\'s reply: {note}',
        role: 'message',
        max: 120,
      },
      {
        key: 'account.request_refund_original',
        label: 'Refunded to the card or UPI',
        value: '{amount} refunded to your original payment method',
        role: 'message',
        max: 120,
      },
      {
        key: 'account.request_refund_manual',
        label: 'Refunded by the store',
        value: '{amount} will be refunded to you by the store',
        role: 'message',
        max: 120,
      },
      {
        key: 'account.request_refund_credit',
        label: 'Refunded as store credit',
        value: '{amount} issued as store credit - gift card {code}',
        role: 'message',
        max: 120,
      },
      {
        key: 'account.item_cancelled',
        label: 'Item, units cancelled',
        value: '{count} cancelled',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.item_returned',
        label: 'Item, units returned',
        value: '{count} returned',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.item_replaced',
        label: 'Item, units replaced',
        value: '{count} replaced',
        role: 'label',
        max: 40,
      },
    ],
  },
  {
    id: 'account-bookings',
    area: 'account',
    label: 'Bookings',
    description: "A shopper's own appointments, under their account.",
    fields: [
      { key: 'account.next_booking', label: 'Overview, next appointment', value: 'Your next appointment', role: 'heading', max: 60 },
      { key: 'account.bookings_upcoming', label: 'Upcoming tab', value: 'Upcoming', role: 'label', max: 30 },
      { key: 'account.bookings_past', label: 'Past tab', value: 'Past', role: 'label', max: 30 },
      {
        key: 'account.bookings_loading',
        label: 'While bookings load',
        value: 'Loading your bookings…',
        role: 'message',
        max: 60,
      },
      {
        key: 'account.no_bookings_title',
        label: 'No upcoming bookings, heading',
        value: 'No upcoming bookings',
        role: 'empty',
        max: 60,
      },
      {
        key: 'account.no_bookings_body',
        label: 'No upcoming bookings, line',
        value: 'When you book an appointment, it will show up here.',
        role: 'empty',
        max: 160,
      },
      {
        key: 'account.no_past_bookings',
        label: 'No past bookings',
        value: 'No past bookings yet.',
        role: 'empty',
        max: 120,
      },
      { key: 'account.book_now', label: 'Book button', value: 'Book an appointment', role: 'button', max: 40 },
      {
        key: 'account.booking_with',
        label: 'Who it is with',
        value: 'With {name}',
        role: 'label',
        hint: '{name} is the person the appointment is with.',
        max: 60,
      },
      {
        key: 'account.booking_token',
        label: 'Token number',
        value: 'Token {number}',
        role: 'label',
        hint: '{number} is the place in the queue.',
        max: 30,
      },
      { key: 'account.booking_online', label: 'Online appointment', value: 'Online', role: 'label', max: 30 },
      {
        key: 'account.booking_at_address',
        label: 'At the shopper’s address',
        value: 'At {address}',
        role: 'label',
        hint: '{address} is the address the shopper gave.',
        max: 60,
      },
      { key: 'account.booking_cancel', label: 'Cancel button', value: 'Cancel booking', role: 'button', max: 40 },
      {
        key: 'account.booking_cancel_confirm',
        label: 'Cancel, are you sure',
        value: 'Cancel this appointment? The time will be offered to someone else.',
        role: 'message',
        max: 160,
      },
      { key: 'account.booking_keep', label: 'Keep it button', value: 'Keep it', role: 'button', max: 30 },
      {
        key: 'account.booking_cancelled',
        label: 'After cancelling',
        value: 'Your appointment has been cancelled.',
        role: 'message',
        max: 120,
      },
      {
        key: 'account.booking_cancel_error',
        label: 'Cancelling failed',
        value: 'We could not cancel that appointment.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'account.booking_change_hint',
        label: 'How to change the time',
        value: 'Need a different time? Cancel this one and book again.',
        role: 'message',
        max: 160,
      },
      { key: 'booking_status.pending', label: 'Awaiting confirmation', value: 'Awaiting confirmation', role: 'label', max: 40 },
      { key: 'booking_status.confirmed', label: 'Appointment confirmed', value: 'Confirmed', role: 'label', max: 40 },
      { key: 'booking_status.cancelled', label: 'Appointment cancelled', value: 'Cancelled', role: 'label', max: 40 },
      { key: 'booking_status.completed', label: 'Appointment completed', value: 'Completed', role: 'label', max: 40 },
      { key: 'booking_status.no_show', label: 'Appointment missed', value: 'Missed', role: 'label', max: 40 },
      {
        key: 'booking_status.awaiting_payment',
        label: 'Held, awaiting payment',
        value: 'Awaiting payment',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.complete_payment',
        label: 'Pay for a held booking',
        value: 'Complete payment',
        role: 'button',
        max: 40,
      },
      {
        key: 'account.booking_hold_until',
        label: 'Held booking, pay by',
        value: 'Pay by {time} to keep this slot.',
        role: 'message',
        hint: '{time} is when the held slot is released.',
        max: 120,
      },
      {
        key: 'account.booking_paid',
        label: 'Booking, amount paid',
        value: 'Paid {amount}',
        role: 'label',
        hint: '{amount} is what the shopper paid.',
        max: 40,
      },
      {
        key: 'account.booking_refunded',
        label: 'Booking, amount refunded',
        value: 'Refunded {amount}',
        role: 'label',
        hint: '{amount} is what was refunded.',
        max: 40,
      },
      {
        key: 'account.booking_package',
        label: 'Booking paid with a package',
        value: 'Paid with a package session',
        role: 'label',
        max: 60,
      },
      {
        key: 'account.booking_pay_at_venue',
        label: 'Booking paid at the venue',
        value: 'Pay at the venue',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.booking_due_at_venue',
        label: 'Balance due at the venue',
        value: '{amount} due at the venue',
        role: 'label',
        hint: '{amount} is what is left to pay there.',
        max: 60,
      },
      {
        key: 'account.booking_refund_full',
        label: 'Cancel, the refund',
        value: 'You’ll be refunded {amount}.',
        role: 'message',
        hint: '{amount} is what cancelling now refunds.',
        max: 120,
      },
      {
        key: 'account.booking_refund_none',
        label: 'Cancel, no refund',
        value: 'It’s past the free-cancellation window, so nothing will be refunded.',
        role: 'message',
        max: 160,
      },
      {
        key: 'account.booking_session_back',
        label: 'Cancel, package session',
        value: 'If you cancel in time, the session goes back on your package.',
        role: 'message',
        max: 160,
      },
    ],
  },
  {
    id: 'account-status',
    area: 'account',
    label: 'Order and parcel status',
    description: 'How each stage of an order and a shipment reads to the shopper.',
    fields: [
      { key: 'order_status.pending', label: 'Order awaiting payment', value: 'Awaiting payment', role: 'label', max: 40 },
      { key: 'order_status.confirmed', label: 'Order confirmed', value: 'Confirmed', role: 'label', max: 40 },
      { key: 'order_status.processing', label: 'Order being prepared', value: 'Being prepared', role: 'label', max: 40 },
      { key: 'order_status.packed', label: 'Order packed', value: 'Packed', role: 'label', max: 40 },
      { key: 'order_status.shipped', label: 'Order shipped', value: 'On its way', role: 'label', max: 40 },
      { key: 'order_status.delivered', label: 'Order delivered', value: 'Delivered', role: 'label', max: 40 },
      { key: 'order_status.cancelled', label: 'Order cancelled', value: 'Cancelled', role: 'label', max: 40 },
      {
        key: 'order_status.return_requested',
        label: 'Return requested',
        value: 'Return requested',
        role: 'label',
        max: 40,
      },
      { key: 'order_status.returned', label: 'Order returned', value: 'Returned', role: 'label', max: 40 },
      { key: 'order_status.refunded', label: 'Order refunded', value: 'Refunded', role: 'label', max: 40 },
      { key: 'shipment_status.pending', label: 'Parcel pending', value: 'Pending', role: 'label', max: 40 },
      { key: 'shipment_status.created', label: 'Parcel booked', value: 'Booked', role: 'label', max: 40 },
      {
        key: 'shipment_status.pickup_scheduled',
        label: 'Pickup scheduled',
        value: 'Pickup scheduled',
        role: 'label',
        max: 40,
      },
      { key: 'shipment_status.picked_up', label: 'Picked up', value: 'Picked up', role: 'label', max: 40 },
      { key: 'shipment_status.in_transit', label: 'In transit', value: 'In transit', role: 'label', max: 40 },
      {
        key: 'shipment_status.out_for_delivery',
        label: 'Out for delivery',
        value: 'Out for delivery',
        role: 'label',
        max: 40,
      },
      { key: 'shipment_status.delivered', label: 'Parcel delivered', value: 'Delivered', role: 'label', max: 40 },
      {
        key: 'shipment_status.delivery_failed',
        label: 'Delivery attempt failed',
        value: 'Delivery attempt failed',
        role: 'label',
        max: 40,
      },
      { key: 'shipment_status.rto', label: 'Returning to seller', value: 'Returning to seller', role: 'label', max: 40 },
      {
        key: 'shipment_status.rto_delivered',
        label: 'Returned to seller',
        value: 'Returned to seller',
        role: 'label',
        max: 40,
      },
      { key: 'shipment_status.returned', label: 'Parcel returned', value: 'Returned', role: 'label', max: 40 },
      { key: 'shipment_status.lost', label: 'Parcel lost', value: 'Lost', role: 'label', max: 40 },
      { key: 'shipment_status.cancelled', label: 'Parcel cancelled', value: 'Cancelled', role: 'label', max: 40 },
    ],
  },
  {
    id: 'account-profile',
    area: 'account',
    label: 'Profile, password and devices',
    fields: [
      { key: 'account.your_details', label: 'Details heading', value: 'Your details', role: 'heading', max: 60 },
      { key: 'account.phone', label: 'Phone field', value: 'Phone', role: 'label', max: 40 },
      {
        key: 'account.email_hint',
        label: 'Email field hint',
        value: 'Your email identifies your account and every order on it.',
        role: 'message',
        max: 160,
      },
      {
        key: 'account.save_error',
        label: 'Saving failed',
        value: 'We could not save that.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'account.password_intro',
        label: 'Password section line',
        value: 'Changing it signs you out everywhere, including here.',
        role: 'message',
        max: 160,
      },
      {
        key: 'account.password_error',
        label: 'Password change failed',
        value: 'We could not change the password.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'account.current_password',
        label: 'Current password field',
        value: 'Current password',
        role: 'label',
        max: 40,
      },
      { key: 'account.new_password', label: 'New password field', value: 'New password', role: 'label', max: 40 },
      {
        key: 'account.new_password_hint',
        label: 'New password hint',
        value: 'At least 10 characters.',
        role: 'message',
        max: 120,
      },
      {
        key: 'account.repeat_password',
        label: 'Repeat password field',
        value: 'Repeat new password',
        role: 'label',
        max: 40,
      },
      { key: 'account.updating', label: 'Password button, working', value: 'Updating…', role: 'button', max: 40 },
      {
        key: 'account.change_password',
        label: 'Change password button',
        value: 'Change password',
        role: 'button',
        max: 40,
      },
      {
        key: 'account.error_current_password',
        label: 'Current password missing',
        value: 'Enter your current password.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'account.error_repeat_password',
        label: 'Repeated password missing',
        value: 'Repeat your new password.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'account.sessions',
        label: 'Devices heading',
        value: 'Where you are signed in',
        role: 'heading',
        max: 60,
      },
      {
        key: 'account.sessions_intro',
        label: 'Devices line',
        value: 'End a session if you do not recognise it, or left yourself signed in somewhere.',
        role: 'message',
        max: 160,
      },
      {
        key: 'account.session_error',
        label: 'Ending one session failed',
        value: 'We could not end that session.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'account.sessions_error',
        label: 'Ending other sessions failed',
        value: 'We could not end those sessions.',
        role: 'validation',
        max: 160,
      },
      { key: 'account.this_device', label: 'This device', value: 'this device', role: 'label', max: 40 },
      {
        key: 'account.unknown_location',
        label: 'Unknown location',
        value: 'Unknown location',
        role: 'label',
        max: 40,
      },
      {
        key: 'account.session_since',
        label: 'Where and since when',
        value: '{location} · since {date}',
        role: 'label',
        hint: '{location} is where the device signed in from, {date} when it was last active.',
        max: 80,
      },
      { key: 'account.session_end', label: 'End session button', value: 'End', role: 'button', max: 30 },
      { key: 'account.signing_out', label: 'Sign out button, working', value: 'Signing out…', role: 'button', max: 40 },
      {
        key: 'account.sign_out_others',
        label: 'Sign out elsewhere button',
        value: 'Sign out every other device',
        role: 'button',
        max: 60,
      },
    ],
  },
  {
    id: 'account-addresses',
    area: 'account',
    label: 'Address book',
    fields: [
      {
        key: 'account.addresses_loading',
        label: 'While addresses load',
        value: 'Loading your addresses…',
        role: 'message',
        max: 60,
      },
      {
        key: 'account.addresses_intro',
        label: 'No saved addresses, line',
        value: 'Save an address and checkout will fill it in for you.',
        role: 'message',
        max: 160,
      },
      {
        key: 'account.addresses_count_one',
        label: 'One address saved',
        value: '{count} address saved.',
        role: 'message',
        hint: '{count} is how many addresses are saved.',
        max: 60,
      },
      {
        key: 'account.addresses_count_many',
        label: 'Several addresses saved',
        value: '{count} addresses saved.',
        role: 'message',
        max: 60,
      },
      { key: 'account.add_address', label: 'Add address button', value: 'Add an address', role: 'button', max: 40 },
      { key: 'account.edit_address', label: 'Edit address heading', value: 'Edit address', role: 'heading', max: 60 },
      {
        key: 'account.no_addresses_title',
        label: 'No addresses, heading',
        value: 'No addresses yet',
        role: 'empty',
        max: 60,
      },
      {
        key: 'account.no_addresses_body',
        label: 'No addresses, line',
        value: 'Anywhere you have had something delivered will be saved here automatically.',
        role: 'empty',
        max: 160,
      },
      { key: 'account.field_name', label: 'Name field', value: 'Name (optional)', role: 'label', max: 40 },
      { key: 'account.field_name_hint', label: 'Name field hint', value: 'Defaults to your own.', role: 'message', max: 80 },
      { key: 'account.field_address', label: 'Address field', value: 'Address', role: 'label', max: 40 },
      {
        key: 'account.use_as_default',
        label: 'Make default checkbox',
        value: 'Use this as my default address',
        role: 'label',
        max: 60,
      },
      { key: 'account.save_address', label: 'Save address button', value: 'Save address', role: 'button', max: 40 },
      { key: 'account.cancel', label: 'Cancel button', value: 'Cancel', role: 'button', max: 30 },
      { key: 'account.edit', label: 'Edit button', value: 'Edit', role: 'button', max: 30 },
      { key: 'account.make_default', label: 'Make default button', value: 'Make default', role: 'button', max: 40 },
      { key: 'account.delete', label: 'Delete button', value: 'Delete', role: 'button', max: 30 },
      {
        key: 'account.address_save_error',
        label: 'Saving an address failed',
        value: 'We could not save that address.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'account.address_delete_error',
        label: 'Deleting an address failed',
        value: 'We could not delete that address.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'account.error_address',
        label: 'Address missing',
        value: 'Enter the address.',
        role: 'validation',
        max: 120,
      },
      { key: 'account.error_city', label: 'City missing', value: 'Enter the city.', role: 'validation', max: 120 },
      { key: 'account.error_state', label: 'State missing', value: 'Enter the state.', role: 'validation', max: 120 },
    ],
  },
  {
    id: 'account-password',
    area: 'account',
    label: 'Resetting a password and confirming an email',
    fields: [
      {
        key: 'account.forgot_title',
        label: 'Forgotten password heading',
        value: 'Forgot your password?',
        role: 'heading',
        max: 60,
      },
      {
        key: 'account.forgot_intro',
        label: 'Forgotten password line',
        value: 'Enter your email address and we will send you a link to set a new one.',
        role: 'message',
        max: 160,
      },
      {
        key: 'account.forgot_error',
        label: 'Link not sent',
        value: 'We could not send the link. Try again.',
        role: 'validation',
        max: 160,
      },
      { key: 'account.sending', label: 'Send button, working', value: 'Sending…', role: 'button', max: 40 },
      { key: 'account.send_reset', label: 'Send link button', value: 'Send reset link', role: 'button', max: 40 },
      {
        key: 'account.reset_sent',
        label: 'Link on its way',
        value:
          'If an account exists for {email}, a reset link is on its way. It expires in an hour and can only be used once.',
        role: 'message',
        hint: '{email} is the address they typed.',
        multiline: true,
        max: 300,
      },
      {
        key: 'account.link_incomplete',
        label: 'Broken link heading',
        value: 'This link is incomplete',
        role: 'heading',
        max: 60,
      },
      {
        key: 'account.reset_link_incomplete_body',
        label: 'Broken reset link, line',
        value: 'Open the link from your email directly, or ask for a new one.',
        role: 'message',
        max: 160,
      },
      { key: 'account.send_new_link', label: 'New link button', value: 'Send a new link', role: 'button', max: 40 },
      { key: 'account.reset_title', label: 'New password heading', value: 'Choose a new password', role: 'heading', max: 60 },
      {
        key: 'account.reset_error',
        label: 'Password not updated',
        value: 'We could not update the password. Try again.',
        role: 'validation',
        max: 160,
      },
      { key: 'account.set_password', label: 'Set password button', value: 'Set new password', role: 'button', max: 40 },
      {
        key: 'account.password_updated',
        label: 'Password updated heading',
        value: 'Password updated',
        role: 'heading',
        max: 60,
      },
      {
        key: 'account.password_updated_body',
        label: 'Password updated, line',
        value: 'Every device that was signed in has been signed out. Sign in again with your new password.',
        role: 'message',
        multiline: true,
        max: 200,
      },
      {
        key: 'account.verify_link_incomplete_body',
        label: 'Broken confirmation link, line',
        value: 'Open the link from your email directly, and it will confirm your address.',
        role: 'message',
        max: 160,
      },
      {
        key: 'account.verifying',
        label: 'While confirming',
        value: 'Confirming your email…',
        role: 'message',
        max: 60,
      },
      {
        key: 'account.verify_failed',
        label: 'Confirmation failed heading',
        value: 'We could not confirm that',
        role: 'heading',
        max: 60,
      },
      {
        key: 'account.verify_failed_body',
        label: 'Confirmation failed, line',
        value: 'The link may have expired or already been used.',
        role: 'message',
        max: 160,
      },
      { key: 'account.go_to_sign_in', label: 'Sign in link', value: 'Go to sign in', role: 'button', max: 40 },
      { key: 'account.verified', label: 'Email confirmed heading', value: 'Email confirmed', role: 'heading', max: 60 },
      {
        key: 'account.verified_body',
        label: 'Email confirmed, line',
        value: 'Your account is ready. Sign in to see your orders.',
        role: 'message',
        max: 160,
      },
    ],
  },
  {
    id: 'account-shopping',
    area: 'account',
    label: 'Buying again and suggestions',
    fields: [
      { key: 'account.buy_again', label: 'Buy it again', value: 'Buy it Again', role: 'heading', max: 40 },
      {
        key: 'account.buy_again_all',
        label: 'See every past purchase',
        value: 'View All & Buy Again',
        role: 'button',
        max: 40,
      },
      { key: 'account.added_to_cart', label: 'Added', value: 'Added to cart', role: 'button', max: 40 },
      {
        key: 'account.add_error',
        label: 'Could not add',
        value: 'Could not add that to your cart.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'account.recommended',
        label: 'Suggestions heading',
        value: 'Recommended based on your shopping trends',
        role: 'heading',
        max: 80,
      },
      { key: 'account.add_to_cart', label: 'Add to cart button', value: 'Add to Cart', role: 'button', max: 40 },
      { key: 'account.choose_options', label: 'Choose options link', value: 'Choose options', role: 'button', max: 40 },
    ],
  },

  // --- The language switcher ----------------------------------------------
  {
    id: 'header-language',
    area: 'header',
    label: 'Language switcher',
    fields: [
      { key: 'language.label', label: 'Switcher name', value: 'Language', role: 'label', max: 30 },
      {
        key: 'language.choose',
        label: 'Switcher prompt',
        value: 'Choose language',
        role: 'label',
        max: 40,
      },
      {
        key: 'language.current',
        label: 'Current language',
        value: 'Current language: {language}',
        role: 'label',
        hint: 'Read aloud by screen readers. {language} is the language name.',
        max: 60,
      },
    ],
  },

  // --- Theme mastheads and footers ----------------------------------------
  {
    id: 'theme-header-search',
    area: 'header',
    label: 'Masthead search and departments',
    description: 'Used by the themes that draw a large search box in the header.',
    fields: [
      {
        key: 'theme.search_catalogue',
        label: 'Search box name',
        value: 'Search the catalogue',
        role: 'label',
        max: 60,
      },
      {
        key: 'theme.search_tools_placeholder',
        label: 'Search box hint',
        value: 'Search tools, machines and parts',
        role: 'placeholder',
        max: 60,
      },
      {
        key: 'theme.shop_all',
        label: 'Whole department link',
        value: 'Shop all {name}',
        role: 'button',
        hint: '{name} is the department.',
        max: 60,
      },
      {
        key: 'theme.mega_card_best_selling',
        label: 'Big menu first picture card',
        value: 'Best Selling',
        role: 'button',
        max: 40,
      },
      {
        key: 'theme.mega_card_new_arrivals',
        label: 'Big menu second picture card',
        value: 'New Arrivals',
        role: 'button',
        max: 40,
      },
    ],
  },
  {
    id: 'theme-footer',
    area: 'global',
    label: 'Footer fine print and columns',
    fields: [
      {
        key: 'theme.powered_by',
        label: 'Platform credit',
        value: 'Powered by {brand}',
        role: 'legal',
        hint: '{brand} is NeonCarts.',
        max: 60,
      },
      { key: 'theme.footer_shop', label: 'Shop column', value: 'Shop', role: 'heading', max: 30 },
      { key: 'theme.departments', label: 'Departments column', value: 'Departments', role: 'heading', max: 30 },
      {
        key: 'theme.footer_continued',
        label: 'A column that runs on',
        value: '{title}, continued',
        role: 'heading',
        hint: '{title} is the column it continues.',
        max: 60,
      },
      {
        key: 'theme.footer_customer_care',
        label: 'Customer care column',
        value: 'Customer care',
        role: 'heading',
        max: 30,
      },
      { key: 'theme.footer_client_care', label: 'Client care column', value: 'Client care', role: 'heading', max: 30 },
      { key: 'theme.footer_support', label: 'Support column', value: 'Support', role: 'heading', max: 30 },
      { key: 'theme.footer_help', label: 'Help column', value: 'Help', role: 'heading', max: 30 },
      { key: 'theme.footer_contact', label: 'Contact column', value: 'Contact us', role: 'heading', max: 30 },
      {
        key: 'theme.footer_we_accept',
        label: 'Payment marks',
        value: 'We accept',
        role: 'label',
        hint: 'Names the ways to pay in the large footer\'s bottom bar.',
        max: 30,
      },
      { key: 'theme.pay_upi', label: 'UPI mark', value: 'UPI', role: 'label', max: 20 },
      { key: 'theme.pay_cards', label: 'Cards mark', value: 'Cards', role: 'label', max: 20 },
      { key: 'theme.pay_cod', label: 'Cash on delivery mark', value: 'Cash on delivery', role: 'label', max: 30 },
      {
        key: 'theme.footer_benefits',
        label: 'Benefits strip',
        value: 'Why shop with us',
        role: 'label',
        hint: 'Read out by screen readers for the scrolling strip above the footer.',
        max: 40,
      },
      {
        key: 'theme.prices_inr_gst',
        label: 'Prices note',
        value: 'Prices in INR, inclusive of GST',
        role: 'legal',
        max: 80,
      },
      {
        key: 'theme.prices_all_inr_gst',
        label: 'Prices note, all prices',
        value: 'All prices in INR, inclusive of GST',
        role: 'legal',
        max: 80,
      },
      {
        key: 'theme.prices_all_inr_gst_included',
        label: 'Prices note, GST included',
        value: 'All prices in INR, GST included',
        role: 'legal',
        max: 80,
      },
      {
        key: 'theme.trade_counter_hours',
        label: 'Opening hours',
        value: 'Trade counter open Mon-Sat, 8am-6pm',
        role: 'message',
        max: 80,
      },
    ],
  },
  {
    id: 'theme-newsletter',
    area: 'global',
    label: 'Footer newsletter sign-up',
    fields: [
      { key: 'theme.newsletter_title', label: 'Heading', value: 'Newsletter', role: 'heading', max: 40 },
      { key: 'theme.newsletter_heading', label: 'Invitation', value: 'Join our table.', role: 'heading', max: 60 },
      {
        key: 'theme.newsletter_offer',
        label: 'Offer',
        value: 'Get 15% off your first order.',
        role: 'promo',
        max: 100,
      },
      {
        key: 'theme.newsletter_thanks',
        label: 'Signed up',
        value: 'Thank you — your code is on its way.',
        role: 'message',
        max: 120,
      },
      {
        key: 'theme.newsletter_placeholder',
        label: 'Email hint',
        value: 'Enter your email',
        role: 'placeholder',
        max: 40,
      },
      { key: 'theme.newsletter_subscribe', label: 'Subscribe button', value: 'Subscribe', role: 'button', max: 30 },
      {
        key: 'theme.newsletter_error',
        label: 'Sign-up failed',
        value: 'That did not go through. Try again in a moment.',
        role: 'validation',
        max: 160,
      },
    ],
  },

  // --- Dead ends and everything else --------------------------------------
  {
    id: 'not-found',
    area: 'notFound',
    label: 'The dead end',
    fields: [
      {
        key: 'not_found.title',
        label: 'Heading',
        value: 'Page not found',
        role: 'heading',
        max: 80,
      },
      {
        key: 'not_found.body',
        label: 'The apology',
        value:
          'The page you were looking for has moved or never existed. Everything else is still here.',
        role: 'empty',
        multiline: true,
        max: 300,
      },
      {
        key: 'not_found.cta',
        label: 'The way back',
        value: 'Back to the shop',
        role: 'button',
        max: 40,
      },
    ],
  },
  {
    id: 'global-common',
    area: 'global',
    label: 'Words on every page',
    fields: [
      {
        key: 'common.loading',
        label: 'While a page loads',
        value: 'Loading…',
        role: 'message',
        max: 40,
      },
      {
        key: 'common.page_of',
        label: 'Which page of how many',
        value: 'Page {page} of {pages}',
        role: 'label',
        hint: '{page} and {pages} are replaced by the numbers.',
        max: 60,
      },
      {
        key: 'common.previous',
        label: 'Previous page',
        value: '← Previous',
        role: 'button',
        max: 40,
      },
      { key: 'common.next', label: 'Next page', value: 'Next →', role: 'button', max: 40 },
      { key: 'common.retry', label: 'Try again', value: 'Try again', role: 'button', max: 40 },
      { key: 'common.close', label: 'Close', value: 'Close', role: 'button', max: 30 },
      { key: 'common.remove', label: 'Remove', value: 'Remove', role: 'button', max: 30 },
      { key: 'common.quantity', label: 'Quantity', value: 'Quantity', role: 'label', max: 30 },
      { key: 'common.price', label: 'Price', value: 'Price', role: 'label', max: 30 },
      {
        key: 'common.continue_shopping',
        label: 'Keep shopping',
        value: 'Continue shopping',
        role: 'button',
        max: 40,
      },
      {
        key: 'common.apply_coupon',
        label: 'Apply a coupon',
        value: 'Apply coupon',
        role: 'button',
        max: 40,
      },
      { key: 'common.wishlist', label: 'Wishlist', value: 'Wishlist', role: 'label', max: 30 },
      { key: 'common.compare', label: 'Compare', value: 'Compare', role: 'label', max: 30 },
      { key: 'common.login', label: 'Sign in', value: 'Sign in', role: 'button', max: 30 },
      { key: 'common.register', label: 'Create account', value: 'Create account', role: 'button', max: 40 },
      { key: 'common.account', label: 'Account', value: 'Account', role: 'label', max: 30 },
      {
        key: 'common.no_products',
        label: 'No products',
        value: 'No products found.',
        role: 'empty',
        max: 120,
      },
      {
        key: 'common.error',
        label: 'Something failed',
        value: 'Something went wrong. Please try again.',
        role: 'message',
        max: 160,
      },
      { key: 'common.yes', label: 'Yes', value: 'Yes', role: 'label', max: 20 },
      { key: 'common.no', label: 'No', value: 'No', role: 'label', max: 20 },
      {
        key: 'common.pages',
        label: 'Page links',
        value: 'Pages',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 40,
      },
      {
        key: 'common.rail_previous',
        label: 'Scroll a row back',
        value: 'Previous {label}',
        role: 'label',
        hint: 'Read aloud by screen readers. {label} is the row’s heading.',
        max: 100,
      },
      {
        key: 'common.rail_more',
        label: 'Scroll a row on',
        value: 'More {label}',
        role: 'label',
        hint: 'Read aloud by screen readers. {label} is the row’s heading.',
        max: 100,
      },
    ],
  },
  {
    id: 'page-sections',
    area: 'global',
    label: 'Page sections',
    description: 'Words the sections on your pages fall back to.',
    fields: [
      {
        key: 'section.hero_welcome',
        label: 'Hero heading, unset',
        value: 'Welcome to {store}',
        role: 'heading',
        hint: '{store} is your shop’s name.',
        max: 80,
      },
      {
        key: 'section.show_image',
        label: 'Gallery dot',
        value: 'Show image {number}',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 40,
      },
      {
        key: 'section.show_slide',
        label: 'Slideshow dot',
        value: 'Show slide {number}',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 40,
      },
      {
        key: 'section.categories_empty',
        label: 'No categories yet',
        value: 'Categories you create will appear here.',
        role: 'empty',
        max: 160,
      },
      {
        key: 'section.rating',
        label: 'Star rating',
        value: '{rating} out of 5',
        role: 'label',
        hint: 'Read aloud by screen readers. {rating} is the number of stars.',
        max: 40,
      },
      { key: 'section.video', label: 'Video name', value: 'Video', role: 'label', max: 40 },
      { key: 'section.field_name', label: 'Contact form, name', value: 'Name', role: 'label', max: 40 },
      { key: 'section.field_comment', label: 'Contact form, message', value: 'Comment', role: 'label', max: 40 },
      {
        key: 'section.contact_error',
        label: 'Contact form failed',
        value: 'That didn’t send. Please try again, or email us directly.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'section.newsletter_placeholder',
        label: 'Newsletter email hint',
        value: 'you@example.com',
        role: 'placeholder',
        max: 60,
      },
      {
        key: 'section.newsletter_name_placeholder',
        label: 'Newsletter name hint',
        value: 'Your name',
        role: 'placeholder',
        max: 60,
      },
      {
        key: 'section.newsletter_done',
        label: 'Newsletter signed up',
        value: 'Thanks - you’re on the list.',
        role: 'message',
        max: 120,
      },
      {
        key: 'section.newsletter_error',
        label: 'Newsletter failed',
        value: 'That didn’t go through. Please try again.',
        role: 'validation',
        max: 160,
      },
      { key: 'section.countdown_days', label: 'Countdown, days', value: 'Days', role: 'label', max: 20 },
      { key: 'section.countdown_hours', label: 'Countdown, hours', value: 'Hours', role: 'label', max: 20 },
      { key: 'section.countdown_minutes', label: 'Countdown, minutes', value: 'Minutes', role: 'label', max: 20 },
      { key: 'section.countdown_seconds', label: 'Countdown, seconds', value: 'Seconds', role: 'label', max: 20 },
      {
        key: 'section.countdown_left',
        label: 'Countdown, time left',
        value: '{days} days, {hours} hours and {minutes} minutes left',
        role: 'label',
        hint: 'Read aloud by screen readers, once a minute.',
        max: 100,
      },
      {
        key: 'section.countdown_left_hours',
        label: 'Countdown, time left (under a day)',
        value: '{hours} hours and {minutes} minutes left',
        role: 'label',
        hint: 'Read aloud by screen readers, once a minute.',
        max: 100,
      },
      {
        key: 'section.lookbook_hotspot',
        label: 'Lookbook, product dot',
        value: 'See {name}',
        role: 'label',
        hint: 'Read aloud by screen readers on each dot of a shoppable photo.',
        max: 60,
      },
      { key: 'section.lookbook_view', label: 'Lookbook, view product', value: 'View product', role: 'button', max: 30 },
      { key: 'section.lookbook_close', label: 'Lookbook, close card', value: 'Close', role: 'button', max: 20 },
      { key: 'section.spotlight_view', label: 'Product spotlight, view product', value: 'View details', role: 'button', max: 30 },
      {
        key: 'section.spotlight_choose',
        label: 'Product spotlight, choose options',
        value: 'Choose options',
        role: 'button',
        hint: 'Shown instead of add to cart for a product that comes in sizes or colours.',
        max: 30,
      },
      { key: 'section.before_label', label: 'Before and after cards, before', value: 'Before', role: 'label', max: 30 },
      { key: 'section.after_label', label: 'Before and after cards, after', value: 'After', role: 'label', max: 30 },
      {
        key: 'section.before_after_slider',
        label: 'Before and after slider',
        value: 'Drag to compare before and after',
        role: 'label',
        hint: 'Read aloud by screen readers on the comparison slider.',
        max: 80,
      },
      { key: 'section.bmi_height', label: 'BMI calculator, height', value: 'Height', role: 'label', max: 30 },
      { key: 'section.bmi_weight', label: 'BMI calculator, weight', value: 'Weight', role: 'label', max: 30 },
      { key: 'section.bmi_cm', label: 'BMI calculator, centimetres', value: 'cm', role: 'label', max: 10 },
      { key: 'section.bmi_kg', label: 'BMI calculator, kilograms', value: 'kg', role: 'label', max: 10 },
      { key: 'section.bmi_ft', label: 'BMI calculator, feet', value: 'ft', role: 'label', max: 10 },
      { key: 'section.bmi_in', label: 'BMI calculator, inches', value: 'in', role: 'label', max: 10 },
      { key: 'section.bmi_lb', label: 'BMI calculator, pounds', value: 'lb', role: 'label', max: 10 },
      { key: 'section.bmi_age', label: 'BMI calculator, age', value: 'Age', role: 'label', max: 30 },
      { key: 'section.bmi_optional', label: 'BMI calculator, optional field', value: 'Optional', role: 'label', max: 20 },
      { key: 'section.bmi_years', label: 'BMI calculator, years', value: 'yrs', role: 'label', max: 10 },
      { key: 'section.bmi_calculate', label: 'BMI calculator, button', value: 'Calculate', role: 'button', max: 30 },
      {
        key: 'section.bmi_invalid_age',
        label: 'BMI calculator, check the age',
        value: 'Please enter an age between 2 and 120, or leave it empty.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'section.bmi_adults_only',
        label: 'BMI calculator, under 18',
        value: 'BMI categories are for adults 18 and over. For children and teens, a doctor reads BMI on age-based charts.',
        role: 'message',
        max: 160,
      },
      {
        key: 'section.bmi_result',
        label: 'BMI calculator, result',
        value: 'Your BMI is {value}',
        role: 'message',
        hint: '{value} is replaced by the worked-out BMI, like 22.5.',
        max: 60,
      },
      { key: 'section.bmi_underweight', label: 'BMI calculator, underweight', value: 'Underweight', role: 'label', max: 30 },
      { key: 'section.bmi_healthy', label: 'BMI calculator, healthy weight', value: 'Healthy weight', role: 'label', max: 30 },
      { key: 'section.bmi_overweight', label: 'BMI calculator, overweight', value: 'Overweight', role: 'label', max: 30 },
      { key: 'section.bmi_obese', label: 'BMI calculator, obese', value: 'Obese', role: 'label', max: 30 },
      {
        key: 'section.bmi_invalid',
        label: 'BMI calculator, check the numbers',
        value: 'Please enter a realistic height and weight.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'section.bmi_scale',
        label: 'BMI calculator, scale',
        value: 'BMI scale',
        role: 'label',
        hint: 'Read aloud by screen readers on the coloured bar under the result.',
        max: 40,
      },
    ],
  },
  {
    id: 'store-locator',
    area: 'global',
    label: 'Store locator',
    description: 'The words around your shops in the Store locator section. The shops themselves are kept on the Store locations screen.',
    fields: [
      { key: 'locator.open_now', label: 'Open badge', value: 'Open now', role: 'label', max: 30 },
      { key: 'locator.closed_now', label: 'Closed badge', value: 'Closed now', role: 'label', max: 30 },
      { key: 'locator.hours', label: 'Hours heading', value: 'Opening hours', role: 'heading', max: 40 },
      { key: 'locator.closed', label: 'Closed day', value: 'Closed', role: 'label', max: 30 },
      { key: 'locator.directions', label: 'Directions link', value: 'Get directions', role: 'button', max: 40 },
      {
        key: 'locator.search',
        label: 'Search box hint',
        value: 'Search by city or PIN code',
        role: 'placeholder',
        max: 60,
      },
      {
        key: 'locator.no_match',
        label: 'Nothing found',
        value: 'No stores match “{query}”.',
        role: 'empty',
        max: 120,
      },
      { key: 'locator.show_on_map', label: 'Show on map', value: 'Show on map', role: 'button', max: 40 },
      {
        key: 'locator.map_title',
        label: 'Map description',
        value: 'Map of {name}',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 80,
      },
    ],
  },
  {
    id: 'promo-popup',
    area: 'global',
    label: 'Promo popup',
    description:
      'The words around your offer in the promo popup. The offer itself is set in Plugins; the email hint, thank-you and error are shared with the newsletter section.',
    fields: [
      {
        key: 'popup.name_placeholder',
        label: 'Name hint',
        value: 'Enter your full name',
        role: 'placeholder',
        max: 60,
      },
      { key: 'popup.close', label: 'Close button', value: 'Close', role: 'label', max: 30 },
    ],
  },
  {
    id: 'color-mode',
    area: 'header',
    label: 'Light and dark mode',
    description: 'The sun and moon button in the header. Switched on in Plugins.',
    fields: [
      { key: 'color_mode.to_dark', label: 'Switch to dark', value: 'Switch to dark mode', role: 'label', max: 40 },
      { key: 'color_mode.to_light', label: 'Switch to light', value: 'Switch to light mode', role: 'label', max: 40 },
    ],
  },
  {
    id: 'app-tab-bar',
    area: 'global',
    label: 'App bar',
    description: 'The bar fixed to the bottom of a phone screen. Switched on in Plugins.',
    fields: [
      { key: 'app_bar.home', label: 'Home tab', value: 'Home', role: 'label', max: 14 },
      { key: 'app_bar.categories', label: 'Categories tab', value: 'Categories', role: 'label', max: 14 },
      { key: 'app_bar.search', label: 'Search tab', value: 'Search', role: 'label', max: 14 },
      { key: 'app_bar.cart', label: 'Cart tab', value: 'Cart', role: 'label', max: 14 },
      { key: 'app_bar.account', label: 'Account tab', value: 'Account', role: 'label', max: 14 },
    ],
  },
  {
    id: 'wishlist',
    area: 'global',
    label: 'Wishlist',
    description: 'The hearts on products, the header link and the wishlist page. Switched on in Plugins.',
    fields: [
      { key: 'wishlist.title', label: 'Page heading', value: 'Wishlist', role: 'heading', max: 60 },
      { key: 'wishlist.header', label: 'Header link', value: 'Wishlist', role: 'label', max: 30 },
      { key: 'wishlist.add', label: 'Save button', value: 'Save to wishlist', role: 'label', max: 40 },
      {
        key: 'wishlist.remove',
        label: 'Unsave button',
        value: 'Remove from wishlist',
        role: 'label',
        max: 40,
      },
      { key: 'wishlist.empty', label: 'Empty list', value: 'Your wishlist is empty.', role: 'empty', max: 80 },
      {
        key: 'wishlist.empty_hint',
        label: 'Empty list hint',
        value: 'Tap the heart on any product to save it here.',
        role: 'empty',
        max: 120,
      },
      { key: 'wishlist.browse', label: 'Browse button', value: 'Browse products', role: 'button', max: 40 },
      {
        key: 'wishlist.sign_in',
        label: 'Sign-in nudge',
        value: 'Sign in to keep your wishlist on every device.',
        role: 'message',
        hint: 'Shown to guests on the wishlist page when the shop has accounts.',
        max: 120,
      },
      {
        key: 'wishlist.error',
        label: 'Could not save',
        value: 'Could not update your wishlist. Please try again.',
        role: 'validation',
        max: 120,
      },
    ],
  },
  {
    id: 'booking-calendar',
    area: 'global',
    label: 'Bookings',
    fields: [
      {
        key: 'booking.duration',
        label: 'Length of a booking',
        value: '{minutes} min',
        role: 'label',
        hint: '{minutes} is the length of the appointment.',
        max: 20,
      },
      { key: 'booking.choose_day', label: 'Day heading', value: 'Choose a day', role: 'heading', max: 40 },
      { key: 'booking.earlier', label: 'Earlier days', value: 'Earlier', role: 'button', max: 30 },
      { key: 'booking.later', label: 'Later days', value: 'Later', role: 'button', max: 30 },
      { key: 'booking.choose_time', label: 'Time heading', value: 'Choose a time', role: 'heading', max: 40 },
      {
        key: 'booking.checking',
        label: 'While times load',
        value: 'Checking what’s free…',
        role: 'message',
        max: 60,
      },
      {
        key: 'booking.load_error',
        label: 'Times would not load',
        value: 'We couldn’t load the times for that day. Please try again.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'booking.none_free',
        label: 'Nothing free',
        value: 'Nothing free on that day. Try another.',
        role: 'empty',
        max: 160,
      },
      {
        key: 'booking.left_one',
        label: 'One place left',
        value: '{count} left',
        role: 'label',
        hint: '{count} is how many places remain.',
        max: 30,
      },
      { key: 'booking.left_many', label: 'Several places left', value: '{count} left', role: 'label', max: 30 },
      {
        key: 'booking.when',
        label: 'Chosen slot',
        value: '{day} at {time}',
        role: 'label',
        hint: '{day} and {time} are the chosen slot.',
        max: 80,
      },
      {
        key: 'booking.when_duration',
        label: 'Chosen slot with its length',
        value: '{day} at {time} · {minutes} minutes',
        role: 'label',
        max: 100,
      },
      {
        key: 'booking.error',
        label: 'Booking failed',
        value: 'That didn’t go through. Please pick a time and try again.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'booking.choose_person',
        label: 'Person heading',
        value: 'Choose a {singular}',
        role: 'heading',
        hint: '{singular} is your word for one person, such as doctor or stylist. A heading set on the section replaces this.',
        max: 60,
      },
      { key: 'booking.any_person', label: 'Anyone free', value: 'Any available', role: 'button', max: 40 },
      {
        key: 'booking.with_person',
        label: 'Who it is with',
        value: 'With {name}',
        role: 'label',
        hint: '{name} is the person the appointment is with.',
        max: 80,
      },
      {
        key: 'booking.no_people',
        label: 'Nobody offers this service',
        value: 'Nobody is taking bookings for this service right now.',
        role: 'empty',
        max: 160,
      },
      { key: 'booking.location_online', label: 'Online service', value: 'Online', role: 'label', max: 40 },
      {
        key: 'booking.location_address',
        label: 'Service at the customer’s address',
        value: 'At your address',
        role: 'label',
        max: 40,
      },
      { key: 'booking.address_field', label: 'Address field', value: 'Your address', role: 'placeholder', max: 60 },
      {
        key: 'booking.address_required',
        label: 'Address missing',
        value: 'Please tell us the address to come to.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'booking.phone_required',
        label: 'Phone number missing',
        value: 'Please enter a phone number we can reach you on.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'booking.choose_service',
        label: 'Service picker in the booking card',
        value: 'Choose a service',
        role: 'heading',
        max: 40,
      },
      {
        key: 'booking.choose_service_hint',
        label: 'Before a service is picked',
        value: 'Choose a service to see the free times.',
        role: 'message',
        max: 120,
      },
      {
        key: 'booking.step_service',
        label: 'Step: service',
        value: 'Service',
        role: 'heading',
        hint: 'The default name of the step-by-step form’s service step. A name set on the section replaces it.',
        max: 40,
      },
      {
        key: 'booking.step_person',
        label: 'Step: person',
        value: 'Who',
        role: 'heading',
        hint: 'Used when the shop has no word for one of its people (such as Doctor or Stylist).',
        max: 40,
      },
      {
        key: 'booking.step_datetime',
        label: 'Step: date and time',
        value: 'Date & time',
        role: 'heading',
        max: 40,
      },
      {
        key: 'booking.step_details',
        label: 'Step: details',
        value: 'Your details',
        role: 'heading',
        max: 40,
      },
      {
        key: 'booking.step_review',
        label: 'Step: review',
        value: 'Review',
        role: 'heading',
        max: 40,
      },
      {
        key: 'booking.next',
        label: 'Next step',
        value: 'Next',
        role: 'button',
        max: 30,
      },
      {
        key: 'booking.back',
        label: 'Previous step',
        value: 'Back',
        role: 'button',
        max: 30,
      },
      {
        key: 'booking.edit',
        label: 'Change an answer',
        value: 'Edit',
        role: 'button',
        max: 30,
      },
      {
        key: 'booking.step_of',
        label: 'Step counter',
        value: 'Step {n} of {total}',
        role: 'label',
        hint: '{n} is the step the shopper is on, {total} how many there are.',
        max: 40,
      },
      {
        key: 'booking.steps_label',
        label: 'Progress (screen readers)',
        value: 'Booking steps',
        role: 'label',
        max: 60,
      },
      {
        key: 'booking.summary_title',
        label: 'Summary heading',
        value: 'Your appointment',
        role: 'heading',
        max: 40,
      },
      {
        key: 'booking.summary_duration',
        label: 'Summary: length',
        value: 'Duration',
        role: 'label',
        max: 30,
      },
      {
        key: 'booking.summary_price',
        label: 'Summary: price',
        value: 'Price',
        role: 'label',
        max: 30,
      },
      {
        key: 'booking.summary_location',
        label: 'Summary: where',
        value: 'Where',
        role: 'label',
        max: 30,
      },
      {
        key: 'booking.not_chosen',
        label: 'Not chosen yet',
        value: 'Not chosen yet',
        role: 'label',
        max: 40,
      },
      {
        key: 'booking.location_place',
        label: 'Service at the shop',
        value: 'In person',
        role: 'label',
        max: 40,
      },
      {
        key: 'booking.morning',
        label: 'Morning times',
        value: 'Morning',
        role: 'heading',
        max: 30,
      },
      {
        key: 'booking.afternoon',
        label: 'Afternoon times',
        value: 'Afternoon',
        role: 'heading',
        max: 30,
      },
      {
        key: 'booking.evening',
        label: 'Evening times',
        value: 'Evening',
        role: 'heading',
        max: 30,
      },
      {
        key: 'booking.no_times',
        label: 'A day with no times',
        value: 'No times this day',
        role: 'label',
        max: 40,
      },
      {
        key: 'booking.select_placeholder',
        label: 'Unanswered choice',
        value: 'Choose an option',
        role: 'placeholder',
        max: 40,
      },
      {
        key: 'booking.answer_required',
        label: 'Question left blank',
        value: 'Please answer “{question}”.',
        role: 'validation',
        hint: '{question} is the question the shopper skipped.',
        max: 160,
      },
      {
        key: 'booking.answer_yes',
        label: 'Ticked box',
        value: 'Yes',
        role: 'label',
        max: 20,
      },
      {
        key: 'booking.answer_no',
        label: 'Unticked box',
        value: 'No',
        role: 'label',
        max: 20,
      },
      {
        key: 'booking.whatsapp_opt_in',
        label: 'WhatsApp updates box',
        value: 'Send me updates about this appointment on WhatsApp',
        role: 'label',
        hint: 'Shown under the phone field when your shop sends appointment messages on WhatsApp.',
        max: 100,
      },
      {
        key: 'booking.whatsapp_opt_in_hint',
        label: 'WhatsApp updates note',
        value: 'We’ll only message you about this booking.',
        role: 'message',
        max: 120,
      },
      {
        key: 'booking.whatsapp_summary',
        label: 'WhatsApp updates on the review step',
        value: 'WhatsApp updates',
        role: 'label',
        max: 40,
      },
      {
        key: 'booking.take_token',
        label: 'Token sessions beside the times',
        value: 'Or take a token',
        role: 'heading',
        hint: 'Shown above the token sessions of people who see patients by token, under the times of everyone else.',
        max: 40,
      },
      {
        key: 'booking.tokens_hint',
        label: 'How tokens work',
        value: 'Seen in order of token. Pick a session to get the next number.',
        role: 'message',
        max: 120,
      },
      {
        key: 'booking.next_token',
        label: 'Next token in a session',
        value: 'Next token #{number}',
        role: 'label',
        hint: '{number} is the token the shopper would get.',
        max: 40,
      },
      {
        key: 'booking.approx',
        label: 'Estimated time',
        value: 'approx. {time}',
        role: 'label',
        hint: '{time} is roughly when the token will be seen.',
        max: 30,
      },
      { key: 'booking.session_full', label: 'Session full', value: 'Full', role: 'label', max: 20 },
      {
        key: 'booking.session_closed',
        label: 'Session no longer taking tokens',
        value: 'Closed',
        role: 'label',
        max: 20,
      },
      {
        key: 'booking.no_sessions',
        label: 'No sessions that day',
        value: 'No sessions on that day. Try another.',
        role: 'empty',
        max: 120,
      },
      { key: 'booking.summary_token', label: 'Summary: token', value: 'Token', role: 'label', max: 30 },
      {
        key: 'booking.your_token',
        label: 'The shopper’s token',
        value: 'Your token: {number}',
        role: 'label',
        hint: '{number} is the token number.',
        max: 40,
      },
      {
        key: 'booking.push_prompt',
        label: 'Appointment reminders offer',
        value: 'Get a reminder on this device before your appointment.',
        role: 'message',
        hint: 'Shown after booking, on shops that offer push notifications.',
        max: 160,
      },
      {
        key: 'booking.push_on',
        label: 'Appointment reminders are on',
        value: "Reminders are on. We'll remind you before your appointment.",
        role: 'message',
        max: 160,
      },
    ],
  },
  {
    id: 'booking-payments',
    area: 'global',
    label: 'Booking payments',
    description: 'Paying for a booking online - a deposit, the full price or a package session - and buying a package from a pricing card.',
    fields: [
      { key: 'booking.summary_payment', label: 'Summary: payment', value: 'Payment', role: 'label', max: 30 },
      { key: 'booking.pay_due_now', label: 'Amount due now', value: 'Due now', role: 'label', max: 30 },
      { key: 'booking.pay_total', label: 'Full price', value: 'Total', role: 'label', max: 30 },
      {
        key: 'booking.pay_at_venue_rest',
        label: 'The rest, at the venue',
        value: 'Pay {amount} at the venue',
        role: 'label',
        hint: '{amount} is what is left to pay there.',
        max: 60,
      },
      {
        key: 'booking.pay_at_venue_all',
        label: 'Nothing due online',
        value: 'Pay at the venue',
        role: 'label',
        max: 40,
      },
      {
        key: 'booking.pay_choice_label',
        label: 'Pay now or later, question',
        value: 'How would you like to pay?',
        role: 'heading',
        max: 60,
      },
      { key: 'booking.pay_now_option', label: 'Pay now option', value: 'Pay now online', role: 'label', max: 40 },
      { key: 'booking.pay_later_option', label: 'Pay later option', value: 'Pay at the venue', role: 'label', max: 40 },
      {
        key: 'booking.use_package',
        label: 'Use a package session',
        value: 'Use 1 of {count} sessions left on {name}',
        role: 'label',
        hint: '{count} is the sessions left; {name} is the package.',
        max: 80,
      },
      {
        key: 'booking.use_package_hint',
        label: 'Using a package, line',
        value: 'Nothing to pay now - a session comes off your package.',
        role: 'message',
        max: 120,
      },
      {
        key: 'booking.package_summary',
        label: 'Summary: paid by package',
        value: 'Package session',
        role: 'label',
        max: 40,
      },
      {
        key: 'booking.paying',
        label: 'While the payment opens',
        value: 'Opening secure payment…',
        role: 'message',
        max: 60,
      },
      { key: 'booking.hold_title', label: 'Held slot, heading', value: 'Your slot is held', role: 'heading', max: 60 },
      {
        key: 'booking.hold_until',
        label: 'Held slot, until when',
        value: 'Held until {time}. Pay to confirm it.',
        role: 'message',
        hint: '{time} is when the hold ends.',
        max: 120,
      },
      {
        key: 'booking.hold_left',
        label: 'Held slot, time left',
        value: '({time} left)',
        role: 'label',
        hint: '{time} is minutes and seconds.',
        max: 30,
      },
      { key: 'booking.pay_retry', label: 'Pay for a held slot', value: 'Pay now', role: 'button', max: 30 },
      {
        key: 'booking.hold_expired',
        label: 'Hold ran out',
        value: 'The hold on this slot ran out, so it may have been taken.',
        role: 'message',
        max: 160,
      },
      { key: 'booking.pick_another', label: 'Choose a new time', value: 'Pick another time', role: 'button', max: 40 },
      {
        key: 'booking.paid_amount',
        label: 'Confirmation, amount paid',
        value: 'Paid {amount}',
        role: 'label',
        hint: '{amount} is what the shopper paid.',
        max: 40,
      },
      {
        key: 'booking.paid_note',
        label: 'Confirmation, payment received',
        value: 'Payment received. Thank you!',
        role: 'message',
        max: 120,
      },
      {
        key: 'booking.sessions_left',
        label: 'Confirmation, sessions left',
        value: 'Sessions left on your package: {count}',
        role: 'message',
        hint: '{count} is the sessions left.',
        max: 80,
      },
      {
        key: 'booking.payment_error',
        label: 'Payment would not start',
        value: 'We couldn’t start the payment. Please try again.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'plans.package_unpaid',
        label: 'Package bought, not paid',
        value: 'Payment not completed. Your package will be ready once it’s paid.',
        role: 'message',
        max: 160,
      },
      {
        key: 'plans.package_error',
        label: 'Package purchase failed',
        value: 'We couldn’t start that purchase. Please try again.',
        role: 'validation',
        max: 120,
      },
    ],
  },
  {
    id: 'account-packages',
    area: 'account',
    label: 'Session packages',
    description: 'A shopper’s session packages under their account, and what is left on each.',
    fields: [
      { key: 'account.packages', label: 'Packages tab', value: 'Packages', role: 'label', max: 30 },
      {
        key: 'account.packages_loading',
        label: 'While packages load',
        value: 'Loading your packages…',
        role: 'message',
        max: 60,
      },
      {
        key: 'account.no_packages_title',
        label: 'No packages, heading',
        value: 'No packages yet',
        role: 'empty',
        max: 60,
      },
      {
        key: 'account.no_packages_body',
        label: 'No packages, line',
        value: 'Buy a pack of sessions and book them one at a time.',
        role: 'empty',
        max: 160,
      },
      {
        key: 'account.package_sessions_left',
        label: 'Sessions left',
        value: '{left} of {total} sessions left',
        role: 'label',
        hint: '{left} and {total} are session counts.',
        max: 60,
      },
      {
        key: 'account.package_expires',
        label: 'Use-by date',
        value: 'Use by {date}',
        role: 'label',
        hint: '{date} is when the package expires.',
        max: 40,
      },
      { key: 'account.package_no_expiry', label: 'Never expires', value: 'No expiry', role: 'label', max: 40 },
      { key: 'account.package_book', label: 'Book from a package', value: 'Book a session', role: 'button', max: 40 },
      {
        key: 'account.package_bought',
        label: 'After buying a package',
        value: 'Thanks - your package is ready to use.',
        role: 'message',
        max: 120,
      },
      {
        key: 'account.package_pending',
        label: 'Package payment pending',
        value: 'We’re waiting for your payment to come through.',
        role: 'message',
        max: 120,
      },
      {
        key: 'package_status.awaiting_payment',
        label: 'Package awaiting payment',
        value: 'Awaiting payment',
        role: 'label',
        max: 40,
      },
      { key: 'package_status.active', label: 'Package active', value: 'Active', role: 'label', max: 40 },
      { key: 'package_status.exhausted', label: 'Package used up', value: 'Used up', role: 'label', max: 40 },
      { key: 'package_status.expired', label: 'Package expired', value: 'Expired', role: 'label', max: 40 },
      { key: 'package_status.cancelled', label: 'Package cancelled', value: 'Cancelled', role: 'label', max: 40 },
      { key: 'package_status.refunded', label: 'Package refunded', value: 'Refunded', role: 'label', max: 40 },
    ],
  },
  {
    id: 'queue-board',
    area: 'global',
    label: 'Token queue screen',
    description: 'The public “Now serving” page at /queue, and its waiting-room TV view.',
    fields: [
      { key: 'queue.title', label: 'Page heading', value: 'Live queue', role: 'heading', max: 40 },
      { key: 'queue.now_serving', label: 'Now serving', value: 'Now serving', role: 'label', max: 30 },
      {
        key: 'queue.last_issued',
        label: 'Last token given',
        value: 'Last token given',
        role: 'label',
        max: 30,
      },
      { key: 'queue.waiting', label: 'Waiting', value: 'Waiting', role: 'label', max: 30 },
      {
        key: 'queue.not_started',
        label: 'Session not started',
        value: 'Not started yet',
        role: 'label',
        max: 30,
      },
      {
        key: 'queue.empty',
        label: 'No queues today',
        value: 'No token queues today.',
        role: 'empty',
        max: 120,
      },
      {
        key: 'queue.load_error',
        label: 'Queue would not load',
        value: 'We couldn’t load the queue. Trying again…',
        role: 'validation',
        max: 120,
      },
      {
        key: 'queue.auto_refresh',
        label: 'Refresh note',
        value: 'Updates automatically',
        role: 'message',
        max: 60,
      },
      { key: 'queue.tv_link', label: 'TV view link', value: 'Waiting-room screen', role: 'button', max: 40 },
      { key: 'queue.everyone', label: 'Back to everyone', value: 'Everyone', role: 'button', max: 30 },
    ],
  },
  {
    id: 'team',
    area: 'global',
    label: 'Team',
    description:
      'The words around your people in the Team section and on each person’s page. The people themselves are kept on the Team screen.',
    fields: [
      { key: 'team.filter_all', label: 'All filter', value: 'All', role: 'button', max: 30 },
      {
        key: 'team.filter_label',
        label: 'Filter description',
        value: 'Filter by speciality',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 60,
      },
      { key: 'team.filters_toggle', label: 'Show filters (phones)', value: 'Filters', role: 'button', max: 30 },
      {
        key: 'team.search_label',
        label: 'Team search box',
        value: 'Search by name or speciality',
        role: 'placeholder',
        max: 60,
      },
      { key: 'team.filter_speciality', label: 'Speciality filter', value: 'Speciality', role: 'label', max: 40 },
      { key: 'team.filter_availability', label: 'Availability filter', value: 'Availability', role: 'label', max: 40 },
      { key: 'team.available_today', label: 'Available today', value: 'Available today', role: 'button', max: 30 },
      { key: 'team.available_week', label: 'Available this week', value: 'This week', role: 'button', max: 30 },
      { key: 'team.weekday_label', label: 'Working day filter', value: 'Working day', role: 'label', max: 40 },
      { key: 'team.weekday_any', label: 'Any working day', value: 'Any day', role: 'label', max: 30 },
      { key: 'team.filter_visit_format', label: 'Visit type filter', value: 'Visit type', role: 'label', max: 40 },
      { key: 'team.visit_in_person', label: 'In person', value: 'In person', role: 'label', max: 30 },
      { key: 'team.visit_online', label: 'Online', value: 'Online', role: 'label', max: 30 },
      { key: 'team.visit_home', label: 'Home visit', value: 'Home visit', role: 'label', max: 30 },
      { key: 'team.filter_experience', label: 'Experience filter', value: 'Experience', role: 'label', max: 40 },
      {
        key: 'team.experience_min',
        label: 'Experience choice',
        value: '{years}+ years',
        role: 'button',
        hint: '{years} is replaced by 5, 10 or 20.',
        max: 30,
      },
      { key: 'team.filter_gender', label: 'Gender filter', value: 'Gender', role: 'label', max: 40 },
      { key: 'team.gender_female', label: 'Female', value: 'Female', role: 'button', max: 30 },
      { key: 'team.gender_male', label: 'Male', value: 'Male', role: 'button', max: 30 },
      { key: 'team.filter_language', label: 'Language filter', value: 'Language', role: 'label', max: 40 },
      {
        key: 'team.results',
        label: 'How many match',
        value: 'Showing {count} of {total}',
        role: 'label',
        hint: '{count} is how many people match; {total} is everyone shown.',
        max: 60,
      },
      { key: 'team.clear_filters', label: 'Clear filters', value: 'Clear filters', role: 'button', max: 30 },
      {
        key: 'team.no_results',
        label: 'Nobody matches',
        value: 'Nobody matches these filters.',
        role: 'empty',
        max: 120,
      },
      {
        key: 'team.experience_years',
        label: 'Years of experience',
        value: '{years} years experience',
        role: 'label',
        hint: '{years} is replaced by the number from the Team screen.',
        max: 40,
      },
      { key: 'team.languages', label: 'Languages', value: 'Languages', role: 'label', max: 40 },
      { key: 'team.profile_tabs', label: 'Profile tabs description', value: 'About this person', role: 'label', hint: 'Read aloud by screen readers.', max: 60 },
      { key: 'team.tab_about', label: 'About tab', value: 'About', role: 'button', max: 30 },
      { key: 'team.tab_education', label: 'Education and career tab', value: 'Education & career', role: 'button', max: 40 },
      { key: 'team.tab_availability', label: 'Availability tab', value: 'Availability', role: 'button', max: 30 },
      { key: 'team.tab_book', label: 'Book tab', value: 'Book', role: 'button', max: 30 },
      { key: 'team.education', label: 'Education heading', value: 'Education', role: 'heading', max: 40 },
      { key: 'team.career', label: 'Career heading', value: 'Career', role: 'heading', max: 40 },
      {
        key: 'team.next_available',
        label: 'Next free day',
        value: 'Next available: {date}',
        role: 'label',
        hint: '{date} is replaced by the first day with a free time.',
        max: 60,
      },
      { key: 'team.book_heading', label: 'Booking heading', value: 'Book an appointment', role: 'heading', max: 60 },
      { key: 'team.view_profile', label: 'View profile button', value: 'View profile', role: 'button', max: 30 },
      { key: 'team.qualifications', label: 'Qualifications', value: 'Qualifications', role: 'label', max: 40 },
      { key: 'team.services', label: 'Services heading', value: 'Services', role: 'heading', max: 40 },
      { key: 'team.hours', label: 'Hours heading', value: 'Hours', role: 'heading', max: 40 },
      { key: 'team.closed', label: 'Day off', value: 'Closed', role: 'label', max: 30 },
      {
        key: 'team.not_found',
        label: 'Person not found',
        value: 'We couldn’t find that person',
        role: 'heading',
        max: 80,
      },
      {
        key: 'team.not_found_body',
        label: 'Person not found, more',
        value: 'They may have moved on, or the link may be wrong.',
        role: 'empty',
        max: 160,
      },
      { key: 'team.not_found_cta', label: 'Back to the shop', value: 'Back to the shop', role: 'button', max: 40 },
    ],
  },
  {
    id: 'careers',
    area: 'global',
    label: 'Careers',
    description:
      'The words around your open positions on the Careers page, each role’s page and the Careers section. The roles themselves are kept on the Careers screen.',
    fields: [
      { key: 'careers.title', label: 'Careers page heading', value: 'Careers', role: 'heading', max: 60 },
      {
        key: 'careers.intro',
        label: 'Careers page introduction',
        value: 'Come and work with us. These are the roles we are hiring for right now.',
        role: 'message',
        multiline: true,
        max: 200,
      },
      { key: 'careers.empty', label: 'No openings', value: 'No open positions right now', role: 'empty', max: 80 },
      {
        key: 'careers.empty_body',
        label: 'No openings, more',
        value: 'Check back soon - new roles are posted here first.',
        role: 'empty',
        max: 160,
      },
      { key: 'careers.view_all', label: 'View all openings link', value: 'View all openings', role: 'button', max: 40 },
      { key: 'careers.view_role', label: 'View role link', value: 'View role', role: 'button', max: 30 },
      { key: 'careers.back', label: 'Back to all openings', value: 'All openings', role: 'button', max: 40 },
      { key: 'careers.apply', label: 'Apply button', value: 'Apply now', role: 'button', max: 30 },
      {
        key: 'careers.apply_subject',
        label: 'Application email subject',
        value: 'Application: {title}',
        role: 'label',
        hint: '{title} is the role.',
        max: 80,
      },
      {
        key: 'careers.no_apply',
        label: 'No way to apply online',
        value: 'To apply, get in touch with us.',
        role: 'message',
        max: 120,
      },
      { key: 'careers.closes', label: 'Closing date', value: 'Apply by {date}', role: 'label', max: 40 },
      { key: 'careers.posted', label: 'Posted date', value: 'Posted {date}', role: 'label', max: 40 },
      { key: 'careers.details', label: 'Role details, for screen readers', value: 'Role details', role: 'label', max: 40 },
      { key: 'careers.type_full_time', label: 'Full-time', value: 'Full-time', role: 'label', max: 30 },
      { key: 'careers.type_part_time', label: 'Part-time', value: 'Part-time', role: 'label', max: 30 },
      { key: 'careers.type_contract', label: 'Contract', value: 'Contract', role: 'label', max: 30 },
      { key: 'careers.type_internship', label: 'Internship', value: 'Internship', role: 'label', max: 30 },
      { key: 'careers.type_temporary', label: 'Temporary', value: 'Temporary', role: 'label', max: 30 },
      { key: 'careers.type_volunteer', label: 'Volunteer', value: 'Volunteer', role: 'label', max: 30 },
      { key: 'careers.workplace_on_site', label: 'On-site', value: 'On-site', role: 'label', max: 30 },
      { key: 'careers.workplace_remote', label: 'Remote', value: 'Remote', role: 'label', max: 30 },
      { key: 'careers.workplace_hybrid', label: 'Hybrid', value: 'Hybrid', role: 'label', max: 30 },
      {
        key: 'careers.not_found',
        label: 'Role not found',
        value: 'We couldn’t find that role',
        role: 'heading',
        max: 80,
      },
      {
        key: 'careers.not_found_body',
        label: 'Role not found, more',
        value: 'It may have been filled, or the link may be wrong.',
        role: 'empty',
        max: 160,
      },
      { key: 'careers.footer_link', label: 'Footer link', value: 'Careers', role: 'label', max: 30 },
      {
        key: 'careers.apply_heading',
        label: 'Apply form heading',
        value: 'Apply for this role',
        role: 'heading',
        max: 80,
      },
      {
        key: 'careers.apply_intro',
        label: 'Apply form introduction',
        value: 'Tell us a little about yourself. Fields marked * are required.',
        role: 'message',
        max: 200,
      },
      {
        key: 'careers.form_name',
        label: 'Full name field',
        value: 'Full name',
        role: 'label',
        max: 40,
      },
      {
        key: 'careers.form_email',
        label: 'Email field',
        value: 'Email',
        role: 'label',
        max: 40,
      },
      {
        key: 'careers.form_phone',
        label: 'Phone field',
        value: 'Phone',
        role: 'label',
        max: 40,
      },
      {
        key: 'careers.form_location',
        label: 'Current location field',
        value: 'Current location',
        role: 'label',
        max: 40,
      },
      {
        key: 'careers.form_experience',
        label: 'Years of experience field',
        value: 'Years of experience',
        role: 'label',
        max: 40,
      },
      {
        key: 'careers.form_linkedin',
        label: 'LinkedIn field',
        value: 'LinkedIn profile',
        role: 'label',
        max: 40,
      },
      {
        key: 'careers.form_portfolio',
        label: 'Portfolio field',
        value: 'Portfolio or website',
        role: 'label',
        max: 40,
      },
      {
        key: 'careers.form_cover_letter',
        label: 'Cover letter field',
        value: 'Cover letter',
        role: 'label',
        max: 40,
      },
      {
        key: 'careers.form_cover_letter_hint',
        label: 'Cover letter hint',
        value: 'Why this role, and why you? A few lines is plenty.',
        role: 'message',
        max: 160,
      },
      {
        key: 'careers.form_resume',
        label: 'Resume field',
        value: 'Resume',
        role: 'label',
        max: 40,
      },
      {
        key: 'careers.form_resume_hint',
        label: 'Resume hint',
        value: 'PDF, DOC or DOCX, up to 5 MB.',
        role: 'message',
        max: 120,
      },
      {
        key: 'careers.form_resume_drop',
        label: 'Resume drop area',
        value: 'Drag your resume here, or',
        role: 'label',
        max: 60,
      },
      {
        key: 'careers.form_resume_choose',
        label: 'Choose a file link',
        value: 'choose a file',
        role: 'button',
        max: 40,
      },
      {
        key: 'careers.form_resume_remove',
        label: 'Remove file button',
        value: 'Remove',
        role: 'button',
        max: 30,
      },
      {
        key: 'careers.form_optional',
        label: 'Optional field marker',
        value: 'optional',
        role: 'label',
        max: 20,
      },
      {
        key: 'careers.form_consent',
        label: 'Consent checkbox',
        value: 'I agree that {store} may store my details to process my application.',
        role: 'legal',
        hint: '{store} is your shop’s name.',
        max: 200,
      },
      {
        key: 'careers.form_submit',
        label: 'Send application button',
        value: 'Send application',
        role: 'button',
        max: 40,
      },
      {
        key: 'careers.form_sending',
        label: 'Sending',
        value: 'Sending…',
        role: 'button',
        max: 40,
      },
      {
        key: 'careers.form_check_fields',
        label: 'Check the fields',
        value: 'Please check the highlighted fields.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'careers.error_required',
        label: 'Required field',
        value: 'Please fill this in.',
        role: 'validation',
        max: 80,
      },
      {
        key: 'careers.error_email',
        label: 'Invalid email',
        value: 'Enter an email address like name@example.com.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'careers.error_phone',
        label: 'Invalid phone',
        value: 'Enter a phone number we can call you on.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'careers.error_url',
        label: 'Invalid web address',
        value: 'Enter a full web address, starting with https://',
        role: 'validation',
        max: 120,
      },
      {
        key: 'careers.error_experience',
        label: 'Invalid years of experience',
        value: 'Enter a whole number of years from 0 to 60.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'careers.error_too_long',
        label: 'Too long',
        value: 'Please keep this to {max} characters or fewer.',
        role: 'validation',
        hint: '{max} is the field’s limit.',
        max: 120,
      },
      {
        key: 'careers.error_resume_required',
        label: 'Resume missing',
        value: 'Please attach your resume.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'careers.error_resume_type',
        label: 'Wrong resume file type',
        value: 'Please attach a PDF, DOC or DOCX file.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'careers.error_resume_size',
        label: 'Resume too large',
        value: 'This file is larger than 5 MB. Please attach a smaller one.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'careers.error_consent',
        label: 'Consent missing',
        value: 'Please tick this box so we can process your application.',
        role: 'validation',
        max: 120,
      },
      {
        key: 'careers.error_duplicate',
        label: 'Already applied',
        value: 'You have already applied for this role. We will be in touch.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'careers.error_closed',
        label: 'Role closed',
        value: 'Sorry - this role is no longer taking applications.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'careers.error_rate_limited',
        label: 'Too many attempts',
        value: 'Too many attempts. Please wait a few minutes and try again.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'careers.error_resume_unavailable',
        label: 'Resume upload unavailable',
        value: 'We couldn’t upload your resume right now. Please try again later.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'careers.error_generic',
        label: 'Could not send',
        value: 'Your application could not be sent. Please try again.',
        role: 'validation',
        max: 160,
      },
      {
        key: 'careers.success_title',
        label: 'Application sent heading',
        value: 'Application sent',
        role: 'heading',
        max: 80,
      },
      {
        key: 'careers.success_body',
        label: 'Application sent message',
        value: 'Thank you for applying. We’ve emailed you a confirmation and will be in touch.',
        role: 'message',
        max: 200,
      },
      {
        key: 'careers.apply_other_ways',
        label: 'Other ways to apply',
        value: 'Or apply another way:',
        role: 'label',
        max: 60,
      },
      {
        key: 'careers.apply_on_site',
        label: 'Apply on our site link',
        value: 'Apply on our site',
        role: 'button',
        max: 40,
      },
      {
        key: 'careers.apply_by_email',
        label: 'Apply by email link',
        value: 'Apply by email',
        role: 'button',
        max: 40,
      },
    ],
  },
  {
    id: 'global-install',
    area: 'global',
    label: 'Install the shop as an app',
    description: 'The app button in the footer and the instructions it opens. {placeholders} in the steps are drawn bold.',
    fields: [
      {
        key: 'pwa.install_store',
        label: 'Footer app button',
        value: 'Install {store}',
        role: 'button',
        hint: '{store} is your shop’s name.',
        max: 80,
      },
      {
        key: 'pwa.open_app',
        label: 'Footer app button, once installed',
        value: 'Open {store} app',
        role: 'button',
        hint: 'Shown on computers where the shop is already installed.',
        max: 80,
      },
      {
        key: 'pwa.app_links',
        label: 'App store buttons, read aloud',
        value: 'Get our app',
        role: 'label',
        hint: 'Names the Google Play and App Store buttons for screen readers.',
        max: 40,
      },
      {
        key: 'pwa.get_it_on',
        label: 'Google Play button, small line',
        value: 'Get it on',
        role: 'button',
        hint: 'Drawn above "Google Play".',
        max: 20,
      },
      {
        key: 'pwa.download_on',
        label: 'App Store button, small line',
        value: 'Download on the',
        role: 'button',
        hint: 'Drawn above "App Store".',
        max: 24,
      },
      { key: 'pwa.install', label: 'Install button', value: 'Install', role: 'button', max: 30 },
      { key: 'pwa.installing', label: 'Install button, working', value: 'Installing…', role: 'button', max: 30 },
      {
        key: 'pwa.how_to_install',
        label: 'Steps dialog name',
        value: 'How to install {store}',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 80,
      },
      {
        key: 'pwa.add_store_home',
        label: 'Steps heading',
        value: 'Add {store} to your home screen',
        role: 'heading',
        max: 80,
      },
      {
        key: 'pwa.ios_step_share',
        label: 'iPhone step 1',
        value: 'Tap the {share} button at the bottom of Safari.',
        role: 'message',
        max: 120,
      },
      { key: 'pwa.ios_share', label: 'Safari’s Share button', value: 'Share', role: 'label', max: 30 },
      {
        key: 'pwa.ios_step_add_home',
        label: 'iPhone step 2',
        value: 'Choose {add_home}.',
        role: 'message',
        max: 120,
      },
      {
        key: 'pwa.ios_add_home',
        label: 'Safari’s Add to Home Screen item',
        value: 'Add to Home Screen',
        role: 'label',
        max: 40,
      },
      {
        key: 'pwa.ios_step_add',
        label: 'iPhone step 3',
        value: 'Tap {add}. The shop appears on your home screen.',
        role: 'message',
        max: 120,
      },
      { key: 'pwa.ios_add', label: 'Safari’s Add button', value: 'Add', role: 'label', max: 30 },
      {
        key: 'pwa.other_step',
        label: 'Other browsers, the step',
        value: "Open your browser's menu and look for {install}, {add_home} or {add_dock}.",
        role: 'message',
        max: 160,
      },
      {
        key: 'pwa.add_home_screen',
        label: 'Browser menu item, home screen',
        value: 'Add to Home screen',
        role: 'label',
        max: 40,
      },
      { key: 'pwa.add_dock', label: 'Browser menu item, Dock', value: 'Add to Dock', role: 'label', max: 40 },
      {
        key: 'pwa.other_note',
        label: 'Other browsers, the reassurance',
        value: 'Not every browser can install a shop. If you cannot find it, bookmarking this page works just as well.',
        role: 'message',
        multiline: true,
        max: 200,
      },
      // Offered once the shop is installed: order notifications on this device.
      {
        key: 'pwa.push_offer',
        label: 'After install, order notifications offer',
        value: 'Get updates about your orders on this device.',
        role: 'message',
        hint: 'Shown once, after the shop is installed, on shops that offer push notifications.',
        max: 160,
      },
      {
        key: 'pwa.push_offer_on',
        label: 'After install, notifications are on',
        value: "Notifications are on. We'll let you know when your orders move.",
        role: 'message',
        max: 160,
      },
      { key: 'pwa.push_offer_dismiss', label: 'After install, not now button', value: 'Not now', role: 'button', max: 40 },
      { key: 'pwa.push_offer_close', label: 'After install, close button (screen readers)', value: 'Close', role: 'label', max: 40 },
    ],
  },
  {
    id: 'blog-pages',
    area: 'blog',
    label: 'The blog',
    fields: [
      {
        key: 'blog.title',
        label: 'Heading without a blog title',
        value: 'Blog',
        role: 'heading',
        max: 60,
      },
      { key: 'blog.not_found', label: 'Unknown blog', value: 'Blog not found', role: 'empty', max: 80 },
      {
        key: 'blog.tagged',
        label: 'Filtered by a tag',
        value: 'Showing posts tagged',
        role: 'label',
        hint: 'Followed by the tag, which can be cleared.',
        max: 60,
      },
      {
        key: 'blog.empty_tag',
        label: 'Nothing with that tag',
        value: 'Nothing tagged that, yet.',
        role: 'empty',
        max: 160,
      },
      { key: 'blog.empty', label: 'No posts yet', value: 'No posts yet. Check back soon.', role: 'empty', max: 160 },
      {
        key: 'blog.pages',
        label: 'Page links',
        value: 'Blog pages',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 40,
      },
      { key: 'blog.featured', label: 'Featured articles', value: 'Featured articles', role: 'heading', max: 40 },
      { key: 'blog.newer', label: 'Newer posts', value: 'Newer', role: 'button', max: 30 },
      { key: 'blog.older', label: 'Older posts', value: 'Older', role: 'button', max: 30 },
    ],
  },
  {
    id: 'post-page',
    area: 'post',
    label: 'A post or page that is missing',
    fields: [
      { key: 'blog.post_not_found', label: 'Unknown post', value: 'Post not found', role: 'empty', max: 80 },
      {
        key: 'blog.post_not_found_body',
        label: 'Unknown post, line',
        value: 'It may have moved, or it is not published yet.',
        role: 'empty',
        max: 200,
      },
      {
        key: 'page.not_found_body',
        label: 'Unknown page, line',
        value: 'This page may have moved, or it is not published yet.',
        role: 'empty',
        max: 200,
      },
    ],
  },
  {
    id: 'theme-content-pages',
    area: 'global',
    label: "Your theme's own pages",
    description:
      'The heading over an About, team, booking or services page your theme designed, shown at that address until you publish a page of your own there.',
    fields: [
      { key: 'page.title_about', label: 'About page heading', value: 'About us', role: 'heading', max: 60 },
      { key: 'page.title_team', label: 'Team page heading', value: 'Our team', role: 'heading', max: 60 },
      {
        key: 'page.title_book',
        label: 'Booking page heading',
        value: 'Book an appointment',
        role: 'heading',
        max: 60,
      },
      { key: 'page.title_services', label: 'Services page heading', value: 'Services', role: 'heading', max: 60 },
    ],
  },
  {
    id: 'global-whatsapp',
    area: 'global',
    label: 'WhatsApp',
    description: 'The chat button in the corner and the help card on the cart and at the till. Shown once WhatsApp is connected.',
    fields: [
      {
        key: 'whatsapp.chat_button',
        label: 'Chat button',
        value: 'Chat with us on WhatsApp',
        role: 'button',
        hint: 'Read aloud by screen readers; the button itself shows the WhatsApp mark.',
        max: 60,
      },
      { key: 'whatsapp.help_title', label: 'Help card heading', value: 'Need help?', role: 'heading', max: 60 },
      {
        key: 'whatsapp.help_body',
        label: 'Help card line',
        value: 'Chat with us on WhatsApp and we’ll help you finish your order.',
        role: 'message',
        multiline: true,
        max: 200,
      },
      { key: 'whatsapp.help_cta', label: 'Help card button', value: 'Chat on WhatsApp', role: 'button', max: 40 },
      {
        key: 'whatsapp.continue_checkout',
        label: 'Continue on WhatsApp button',
        value: 'Continue checkout on WhatsApp',
        role: 'button',
        hint: 'Offered at the till, when finishing the order in the chat is switched on.',
        max: 60,
      },
    ],
  },
  {
    id: 'global-maintenance',
    area: 'global',
    label: 'During platform maintenance',
    description:
      'Shown only while the platform is briefly down for maintenance: the page in place of the shop, and the note on checkout and booking buttons while orders are paused.',
    fields: [
      { key: 'maintenance.title', label: 'Heading', value: 'We’ll be back soon', role: 'heading', max: 80 },
      {
        key: 'maintenance.body',
        label: 'Explanation',
        value: 'We’re making a few improvements to the shop. Thanks for your patience.',
        role: 'message',
        multiline: true,
        max: 300,
      },
      {
        key: 'maintenance.back_by',
        label: 'Expected time',
        value: 'Expected back by {time}',
        role: 'message',
        hint: '{time} is the expected end, such as 03:00 IST.',
        max: 80,
      },
      {
        key: 'maintenance.back_shortly',
        label: 'No expected time',
        value: 'We expect to be back shortly.',
        role: 'message',
        hint: 'Shown when there is no expected end, or it has passed.',
        max: 80,
      },
      {
        key: 'maintenance.checkout_paused',
        label: 'Checkout paused',
        value: 'Checkout is paused briefly - please try again after {time}.',
        role: 'message',
        hint: 'Under the place-order and booking buttons while orders are paused. {time} is the expected end.',
        max: 160,
      },
      {
        key: 'maintenance.checkout_paused_shortly',
        label: 'Checkout paused, no expected time',
        value: 'Checkout is paused briefly - please try again in a few minutes.',
        role: 'message',
        max: 160,
      },
      { key: 'maintenance.retry', label: 'Retry button', value: 'Try again', role: 'button', max: 40 },
    ],
  },
  {
    id: 'global-store-not-found',
    area: 'global',
    label: 'When no shop is found',
    fields: [
      {
        key: 'store_not_found.title',
        label: 'Heading',
        value: 'This store isn’t available',
        role: 'heading',
        max: 80,
      },
      {
        key: 'store_not_found.body',
        label: 'Explanation',
        value:
          'We couldn’t find a shop at this address. The link may be mistyped, or the store may have closed or moved.',
        role: 'empty',
        multiline: true,
        max: 300,
      },
      {
        key: 'store_not_found.address',
        label: 'Address the shopper asked for',
        value: 'You were looking for',
        role: 'label',
        hint: 'Shown above the address that did not lead to a shop.',
        max: 60,
      },
      { key: 'store_not_found.home', label: 'Platform link', value: 'Go to NeonCarts', role: 'button', max: 40 },
      { key: 'store_not_found.retry', label: 'Retry button', value: 'Try again', role: 'button', max: 40 },
    ],
  },
  {
    id: 'global-store-closed',
    area: 'global',
    label: 'When the shop is closed for now',
    description: 'Shown to shoppers while the store is suspended, until its plan is renewed.',
    fields: [
      {
        key: 'store_closed.title',
        label: 'Heading',
        value: 'This store is temporarily closed',
        role: 'heading',
        max: 80,
      },
      {
        key: 'store_closed.body',
        label: 'Explanation',
        value: 'Please check back soon.',
        role: 'empty',
        multiline: true,
        max: 300,
      },
    ],
  },
];

export const SHOP_CONTENT: ContentManifest = { namespace: SHOP_NAMESPACE, groups };
